import { Component, ElementRef, Input, ViewChild, OnDestroy, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import neo4j from 'neo4j-driver';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NetworkAnalysisConfig } from '../../interfaces/network-analysis-config';

@Component({
  selector: 'shared-network-analysis-graph',
  standalone: true,
  imports: [CommonModule, FormsModule, TooltipModule,TranslatePipe],
  templateUrl: './network-analysis-graph.component.html',
  styleUrl: './network-analysis-graph.component.scss'
})
export class SharedNetworkAnalysisGraphComponent implements OnInit, OnDestroy {
  @ViewChild('container', { static: true }) container!: ElementRef;
  @Input() config!: NetworkAnalysisConfig;
  @Input() cypher!: string;
  @Input() params: any = {};

  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  private graphInstance: any;
  private graphInitPromise: Promise<void> | null = null;
  private resizeObserver!: ResizeObserver;
  private keyboardListener?: (event: KeyboardEvent) => void;
  private prevBodyOverflow: string | null = null;
  private prevLayoutMainOverflow: string | null = null;
  private layoutMainEl: HTMLElement | null = null;
  // Distance from top to clear the app's global header (in px)
  headerOffset = 0;
  private windowResizeHandler?: () => void;
  // Debug flag for verbose Neo4j diagnostics
  // Make debug togglable from template
  neo4jDebug: boolean = true;

  splashTitle: string = 'Network Analysis';
  splashSubtitle: string = 'Select a starting point to begin your investigation.';

  selectedProvider: { id: number; elementId?: string; name: string; } | null = null;
  selectedTop20Entity: string | null = null;
  showDownloadDropdown = false;

  private get providersCacheKey(): string {
    return `${this.config?.cacheKeyPrefix || 'default'}.networkAnalysis.providers.v1`;
  }

  // Neo4j connection settings from config
  private get neo4jUri(): string {
    return this.config?.neo4j?.uri || 'bolt://localhost:7687';
  }
  private get neo4jUser(): string {
    return this.config?.neo4j?.user || 'neo4j';
  }
  private get neo4jPassword(): string {
    return this.config?.neo4j?.password || '';
  }
  private get neo4jConnectTimeoutMs(): number {
    return this.config?.neo4j?.connectionTimeout || 60_000;
  }
  private get neo4jQueryTimeoutMs(): number {
    return this.config?.neo4j?.queryTimeout || 180_000;
  }

  private createNeo4jDriver(): any {
    return neo4j.driver(
      this.neo4jUri,
      neo4j.auth.basic(this.neo4jUser, this.neo4jPassword),
      {
        connectionTimeout: this.neo4jConnectTimeoutMs,
        connectionAcquisitionTimeout: this.neo4jQueryTimeoutMs
      } as any
    );
  }

  private runCypher(session: any, cypher: string, params?: any): Promise<any> {
    // Neo4j driver supports statement transaction config with `timeout`.
    // Use int(ms) when available.
    const timeoutVal = (neo4j as any)?.int ? (neo4j as any).int(this.neo4jQueryTimeoutMs) : this.neo4jQueryTimeoutMs;
    return session.run(cypher, params ?? {}, { timeout: timeoutVal } as any);
  }

  private configureForces(): void {
    if (!this.graphInstance) return;
    // Make nodes repel each other more strongly
    this.graphInstance.d3Force('charge').strength(-400);
    // Increase link distance
    this.graphInstance.d3Force('link').distance(150);
  }

  async renderEmptyGraph() {
    console.log('[NetworkAnalysis] renderEmptyGraph called. graphInstance exists:', !!this.graphInstance, 'container exists:', !!this.container?.nativeElement);
    // Create the graph instance if it doesn't exist yet
    if (!this.graphInstance && this.container?.nativeElement) {
      if (!this.graphInitPromise) {
        console.log('[NetworkAnalysis] Starting new graph instance creation...');
        this.graphInitPromise = this.renderWith2DForceGraph(this.container.nativeElement, [], []);
      } else {
        console.log('[NetworkAnalysis] Graph init already in progress, waiting...');
      }
      await this.graphInitPromise;
      console.log('[NetworkAnalysis] Graph instance created:', !!this.graphInstance);
    } else if (this.graphInstance) {
      console.log('[NetworkAnalysis] Graph instance already exists, updating with empty data');
      this.graphInstance.graphData({ nodes: [], links: [] });
    } else {
      console.warn('[NetworkAnalysis] No container available for graph rendering');
    }
  }

  private async ensureGraphInstance(): Promise<void> {
    // If already initialized, return immediately
    if (this.graphInstance) {
      return;
    }
    
    // If initialization is in progress, wait for it
    if (this.graphInitPromise) {
      console.log('[NetworkAnalysis] Graph initialization in progress, waiting...');
      await this.graphInitPromise;
      return;
    }
    
    // Start initialization
    if (this.container?.nativeElement) {
      console.log('[NetworkAnalysis] Graph instance not ready, initializing now...');
      this.graphInitPromise = this.renderWith2DForceGraph(this.container.nativeElement, [], []);
      await this.graphInitPromise;
      console.log('[NetworkAnalysis] Graph instance initialized');
    }
  }

  showSplash() {
    this.showSplashScreen = true;
  }

  // Query Builder state
  showQueryBuilder = false;
  showSplashScreen = true;
  // Tabs: 'simple' friendly form or 'advanced' raw editor
  builderTab: 'simple' | 'advanced' = 'simple';
  customCypher: string = '';
  customParamsText: string = '{}';
  queryError: string = '';
  queryTemplates: Array<{ label: string; cypher: string; params?: any }> = [
    {
      label: 'NETWORK_ANALYSIS.NEIGHBORHOOD_AROUND_A_NODE',
      cypher: 'MATCH (n) WHERE elementId(n) = $id MATCH p = (n)-[r*1..2]-(m) RETURN nodes(p) AS nodes, relationships(p) AS rels',
      params: { id: '' }
    }
  ];

  // Preset queries from config
  get presetQueries() {
    return this.config?.presetQueries || [];
  }

  // Builder schema metadata (loaded on demand)
  availableLabels: string[] = [];
  availableRelTypes: string[] = [];
  // Fixed entity options (display) mapped to actual DB labels
  entityOptions: Array<{ label: string; value: string }> = [];

  private computeEntityOptions(): void {
    if (!this.config?.actors) {
      this.entityOptions = [];
      return;
    }
    this.entityOptions = this.config.actors.map(actor => ({
      label: actor.displayName,
      value: actor.label
    }));
  }
  // Property Key dropdown: keep only Id, name, Element Id
  availableLabelProps: string[] = ['Id', 'name', 'Element Id'];
  private lastPropKeysLabel: string = '';

  // Simple builder model
  builderMode: 'neighborhood' | 'label' | 'relType' | 'shortestPath' | 'frequency' = 'frequency';
  builder = {
    neighborhood: { id: '', name: '', entityLabel: '' as string, useName: true as boolean, hops: 2 as number, direction: 'any' as 'any' | 'out' | 'in', maxPaths: 600 as number, fastMode: true as boolean, maxNodes: 2000 as number, maxRels: 4000 as number },
    label: {
      label: '',
      propKey: '',
      propValue: '',
      direction: 'any' as 'any' | 'out' | 'in',
      relTypesText: '', // pipe-separated or comma list, we'll parse
      limit: 200 as number
    },
    relType: { type: '', direction: 'any' as 'any' | 'out' | 'in', minWeight: 0 as number, limit: 200 as number },
    shortestPath: { id1: '', id2: '', name1: '', name2: '', useName: true as boolean, maxHops: 3 as number },
    frequency: {
      startingNode: '',
      useNodeName: true as boolean,
      entityLabel: '' as string,
      minFrequencyWeight: 5 as number,
      maxDepth: 2 as number,
      includeHubs: true as boolean,
      topNodesLimit: 100 as number,
      relationshipType: 'CO_OCCURS_WITH' as string
    }
  };

  // Live preview of generated Cypher/params for simple builder
  previewCypher: string = '';
  previewParamsText: string = '';

  private escapeRegExp(input: string): string {
    return String(input).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private toCypherLiteral(value: any): string {
    if (value == null) return 'null';
    // Neo4j Integer
    try {
      if ((neo4j as any)?.integer?.inSafeRange?.(value)) {
        return String((neo4j as any).integer.toNumber(value));
      }
      if (value && typeof value === 'object' && typeof (value as any).toNumber === 'function') {
        return String((value as any).toNumber());
      }
      if (value && typeof value === 'object' && 'low' in value && 'high' in value) {
        const v: any = value;
        return v.high === 0 ? String(v.low) : String(v.low);
      }
    } catch {
      // fallthrough
    }

    if (typeof value === 'number') return isFinite(value) ? String(value) : 'null';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'string') return `'${value.replace(/'/g, "''")}'`;
    if (Array.isArray(value)) return `[${value.map(v => this.toCypherLiteral(v)).join(', ')}]`;

    if (typeof value === 'object') {
      // best-effort map literal
      const entries = Object.entries(value)
        .filter(([k]) => /^[A-Za-z_][A-Za-z0-9_]*$/.test(k))
        .map(([k, v]) => `${k}: ${this.toCypherLiteral(v)}`);
      return `{ ${entries.join(', ')} }`;
    }

    return `'${String(value).replace(/'/g, "''")}'`;
  }

  private inlineCypherParams(cypher: string, params: any): string {
    if (!cypher || !params || typeof params !== 'object') return cypher || '';

    let out = String(cypher);
    const keys = Object.keys(params).sort((a, b) => b.length - a.length);
    for (const key of keys) {
      const literal = this.toCypherLiteral((params as any)[key]);
      // Replace $paramName occurrences.
      out = out.replace(new RegExp(`\\$${this.escapeRegExp(key)}\\b`, 'g'), literal);
    }
    return out;
  }

  // --- Name suggestions for Neighborhood (typeahead) ---
  nameSuggestions: Array<{ display: string; id?: number; elementId?: string; labels?: string[] }> = [];
  suggestionsLoading: boolean = false;
  private nameSuggestDebounce: any = null;
  private readonly suggestMinLen = 2;
  private readonly suggestLimit = 10;

  onNeighborhoodNameInput(): void {
    const term = (this.builder.neighborhood.name || '').trim();
    this.onSimpleFieldChange();
    const hasLabel = !!(this.builder.neighborhood.entityLabel || '').trim();
    if (!hasLabel) {
      // Require entity selection before suggesting
      this.nameSuggestions = [];
      this.suggestionsLoading = false;
      if (this.nameSuggestDebounce) clearTimeout(this.nameSuggestDebounce);
      return;
    }
    if (!this.builder.neighborhood.useName) {
      this.nameSuggestions = [];
      this.suggestionsLoading = false;
      if (this.nameSuggestDebounce) clearTimeout(this.nameSuggestDebounce);
      return;
    }
    if (this.nameSuggestDebounce) clearTimeout(this.nameSuggestDebounce);
    if (term.length < this.suggestMinLen) {
      this.nameSuggestions = [];
      this.suggestionsLoading = false;
      return;
    }
    this.suggestionsLoading = true;
    this.nameSuggestDebounce = setTimeout(() => {
      this.fetchNameSuggestions(term).catch(() => {
        this.suggestionsLoading = false;
      });
    }, 250);
  }

  onEntityLabelChange(): void {
    // Refresh suggestions when the target entity changes
    this.onNeighborhoodNameInput();
  }

  private buildNameExprForLabel(label: string, variable = 'n'): string {
    console.log(`[NetworkAnalysis] buildNameExprForLabel: Building expression for label "${label}" with variable "${variable}".`);
    const actor = this.config.actors.find(a => a.label === label);
    if (!actor?.namePropertyExpression) {
      const defaultExpr = `${variable}.name`;
      console.warn(`[NetworkAnalysis] buildNameExprForLabel: No namePropertyExpression for label "${label}". Falling back to default: "${defaultExpr}".`);
      return defaultExpr;
    }
    const finalExpr = actor.namePropertyExpression.replace(/{{VAR}}/g, variable);
    console.log(`[NetworkAnalysis] buildNameExprForLabel: Found expression template: "${actor.namePropertyExpression}". Final expression: "${finalExpr}".`);
    return finalExpr;
  }

  private isGroupToken(val: string | null | undefined): val is `__grp_${string}` {
    return !!val && val.startsWith('__grp_');
  }

  private buildLabelWhereForGroup(token: string): string {
    if (!this.config?.actors) {
      return 'true';
    }
    const t = token.replace('__grp_', '');
    const actor = this.config.actors.find(a => a.label.toLowerCase() === t.toLowerCase());
    if (!actor?.aliases || actor.aliases.length === 0) {
      return 'true';
    }
    const conditions = actor.aliases.map(alias => {
      const aliasLower = alias.toLowerCase();
      return `toLower(lbl) CONTAINS '${aliasLower}'`;
    }).join(' OR ');
    return `ANY(lbl IN labels(n) WHERE ${conditions})`;
  }

  private safeLabelIdent(name: string): string | null {
    if (!name) return null;
    const trimmed = name.trim();
    // Group tokens like '__grp_provider' are UI-only and must NOT be used as Neo4j labels
    if (this.isGroupToken(trimmed)) return null;
    if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(trimmed)) return trimmed;
    // Quote label with backticks when it contains special chars/spaces
    const escaped = trimmed.replace(/`/g, '``');
    return `\`${escaped}\``;
  }

  private async fetchNameSuggestions(term: string): Promise<void> {
    const label = (this.builder.neighborhood.entityLabel || '').trim();
    const safeLabel = this.safeLabelIdent(label);
    if (!safeLabel && !this.isGroupToken(label)) {
      // No valid label or group; do not query or suggest
      this.nameSuggestions = [];
      this.suggestionsLoading = false;
      return;
    }
    // Instant local fallback using preloaded providers
    const t = term.toLowerCase();
    const primaryLabel = this.config?.primaryEntityLabel || '';
    if ((safeLabel && safeLabel.replace(/`/g, '') === primaryLabel) || (this.isGroupToken(label) && label === `__grp_${primaryLabel.toLowerCase()}`)) {
      const local = (this.mainProviders || [])
        .filter(p => (p.name || '').toLowerCase().includes(t))
        .slice(0, this.suggestLimit)
        .map(p => ({ display: p.name, id: p.id, elementId: p.elementId, labels: [primaryLabel] }));
      if (local.length) {
        this.nameSuggestions = local;
      }
    }

    const driver = this.createNeo4jDriver();
    const session = driver.session();
    try {
      const matchHead = safeLabel ? `MATCH (n:${safeLabel})` : 'MATCH (n)';
      const nameExpr = safeLabel ? this.buildNameExprForLabel(safeLabel, 'n') : `coalesce(n.ProviderName, n.AgentName, n.DoctorName, n.PhysicianName, n.FullName, n.fullName, n.insuredName, n.CustomerName, n.ClientName, n.Name, n.name, trim(coalesce(n.FirstName,'') + ' ' + coalesce(n.LastName,'')), trim(coalesce(n.GivenName,'') + ' ' + coalesce(n.Surname,'')))`;
      const labelWhere = (!safeLabel && this.isGroupToken(label)) ? this.buildLabelWhereForGroup(label) : 'true';
      const cypher = `${matchHead}
        WHERE (${labelWhere}) AND toLower(${nameExpr}) CONTAINS toLower($term)
        RETURN ${nameExpr} AS display, id(n) AS id, elementId(n) AS elementId, labels(n) AS labels
         LIMIT toInteger($limit)`;
      const res = await this.runCypher(session, cypher, { term, limit: Math.floor(this.suggestLimit) });
      const remote = (res.records || []).map((r: any) => {
        const display = r.get('display') ?? '';
        const idVal = r.get('id');
        const elementId = r.get('elementId');
        const labels = r.get('labels');
        const idNumber = (idVal && typeof (idVal as any)?.toNumber === 'function') ? (idVal as any).toNumber() : (typeof idVal === 'number' ? idVal : Number(idVal));
        return {
          display: String(display || ''),
          id: isFinite(idNumber) ? idNumber : undefined,
          elementId: elementId ? String(elementId) : undefined,
          labels: Array.isArray(labels) ? labels.map(String) : []
        };
      });
      if (remote.length) {
        this.nameSuggestions = remote;
      }
    } catch (e) {
      console.warn('[Neo4j] Name suggestions failed:', e);
      // keep local fallback suggestions if present
    } finally {
      this.suggestionsLoading = false;
      try { await session.close(); } catch {}
      try { await driver.close(); } catch {}
    }
  }

  applyNameSuggestion(s: { display: string; id?: number; elementId?: string }): void {
    // Prefer ID-based lookup when available (more stable than free-text names)
    const idNum = Number((s as any)?.id);
    if (isFinite(idNum) && idNum >= 0) {
      this.builder.neighborhood.useName = false;
      this.builder.neighborhood.id = String(Math.floor(idNum));
    } else {
      this.builder.neighborhood.useName = true;
      this.builder.neighborhood.name = s.display || '';
    }
    this.nameSuggestions = [];
    this.onSimpleFieldChange();
  }

  openQueryBuilder() {
    this.showSplashScreen = false;
    this.showQueryBuilder = true;
    // Default to simple tab for regular users
    this.builderTab = 'simple';
    // Prefill simple builder with current selection if any
    const selId = this.selectedNodeId || '';
    const selName = this.selectedNodeName || '';
    if (/^\d+$/.test(selId)) { // only prefill when it's a numeric Neo4j internal id
      this.builder.neighborhood.id = selId;
      this.builder.shortestPath.id1 = selId;
    } else {
      this.builder.neighborhood.id = '';
    }
    if (selName) {
      this.builder.neighborhood.name = selName;
      this.builder.shortestPath.name1 = selName;
    }
    // Populate Entity Type immediately (even if DB metadata is slow/unavailable)
    this.computeEntityOptions();
    this.cdr.markForCheck();
    // Initialize advanced editor mirror
    this.customCypher = this.cypher || '';
    try { this.customParamsText = JSON.stringify(this.params || {}, null, 2); }
    catch { this.customParamsText = '{}'; }
    this.queryError = '';
    // Load labels/rel types once per open for better UX (best-effort) and refresh Entity Type when it arrives
    this.loadSchemaMeta()
      .then(() => this.computeEntityOptions())
      .catch(() => { /* best-effort */ })
      .finally(() => {
        this.updateBuilderPreview();
        this.cdr.markForCheck();
      });
  }

  selectPresetQuery(preset: any) {

    this.showSplashScreen = false;
    this.cypher = preset.cypher;
    this.params = preset.params || {};
    // this.currentQueryDescription = preset.description;
    this.currentQueryDescription = this.t(preset.description);
    
    console.log('[Neo4j] Executing preset query:', {
      title: preset.title,
      cypher: preset.cypher,
      params: preset.params
    });

    console.log('[Neo4j] Current graph instance state:', {
      hasGraphInstance: !!this.graphInstance,
      hasContainer: !!this.container,
      containerElement: this.container?.nativeElement
    });

    // Execute the preset query
    this.isLoading = true;
    this.fetchGraphData()
      .then(async ({ nodes, links }) => {
        console.log('[Neo4j] Preset query result:', {
          nodes: nodes.length,
          links: links.length,
          sampleNode: nodes[0],
          sampleLink: links[0]
        });

        if (nodes.length === 0 && links.length === 0) {
          console.warn('[Neo4j] Preset query returned no data. Query might not match database schema.');
          // Show a message to user but don't fail completely
          // this.currentQueryDescription = `${preset.description} (No data found - database might be empty or query needs adjustment)`;
          this.currentQueryDescription = `${this.t(preset.description)} — ${this.t('NETWORK_ANALYSIS.WARNING_NO_DATA_FOUND')}`;
        }

        this.graphData = { nodes, links };

        // Ensure graph instance is ready before updating
        await this.ensureGraphInstance();

        if (this.graphInstance) {

          // Pause physics while swapping data to avoid jitter
          try { this.graphInstance.pauseAnimation?.(); } catch { }
          // Drop any stale physics positions so the new layout doesn't jump off-screen
          this.resetNodePhysics(this.graphData.nodes);
          this.graphInstance.graphData({ nodes: this.graphData.nodes, links: this.graphData.links });
          this.configureForces();

          // IMPORTANT: Force the simulation to start properly (this was the key fix!)
          setTimeout(() => {

            this.graphInstance.d3ReheatSimulation();

            // Set a higher alpha target to ensure simulation runs
            if (this.graphInstance.d3AlphaTarget) {
              this.graphInstance.d3AlphaTarget(0.3);
            }

            // Resume animation in case it was paused
            this.graphInstance.resumeAnimation();
          }, 50);
          setTimeout(() => {
            // Restart the simulation to ensure nodes get positions
            this.graphInstance.d3ReheatSimulation();
          }, 50);

          // Ensure nodes are not pinned from a previous view
          this.unpinAllNodes();
          // Re-apply center force to container center and then fit
          const container = this.container?.nativeElement as HTMLElement | undefined;
          if (container && (window as any).d3?.forceCenter) {
            const rect = container.getBoundingClientRect();
            this.graphInstance.d3Force('center', (window as any).d3.forceCenter(rect.width / 2, rect.height / 2));

            // Auto-fit viewport to show all nodes
            if (nodes.length > 0) {
              setTimeout(() => {
                // Try a simpler approach - just center and zoom to a reasonable level
                const container = this.container?.nativeElement;
                const cx = container ? container.clientWidth / 2 : 679;
                const cy = container ? container.clientHeight / 2 : 400;

                this.graphInstance.centerAt(cx, cy, 300);
                this.graphInstance.zoom(1, 300);

                // Also try the original method
                setTimeout(() => {
                  this.safeZoomToFit(300);
                }, 500);
              }, 300);
            }
          }
        } else {
          console.warn('[Neo4j] No graph instance available to update');
        }
        this.isLoading = false;
      })
      .catch((error) => {
        console.error('[Neo4j] Query execution error:', error);
        const reason = error?.message || this.t('NETWORK_ANALYSIS.UNKNOWN_ERROR');
        this.currentQueryDescription = `${this.t(preset.description)} — ${this.t('NETWORK_ANALYSIS.QUERY_FAILED_WITH_REASON', { reason })}`;
        this.isLoading = false;
      });
  }

  closeSplashScreen() {
    this.showSplashScreen = false;
  }

  closeQueryBuilder() {
    this.showQueryBuilder = false;
    // If no query has been executed yet, return to splash screen
    if (!this.cypher || this.cypher.trim().length === 0) {
      this.showSplashScreen = true;
    }
  }
  applyQueryBuilder() {
    this.queryError = '';
    if (this.builderTab === 'simple') {
      const built = this.buildSimpleCypher();
      if (!built.ok) {
        this.queryError = built.error || this.t('NETWORK_ANALYSIS.ERROR_COMPLETE_FORM');
        return;
      }
      this.cypher = built.cypher!.trim();
      this.params = built.params || {};
    } else {
      let parsed: any = {};
      try {
        parsed = this.customParamsText?.trim() ? JSON.parse(this.customParamsText) : {};
      } catch (e: any) {
        this.queryError = this.t('NETWORK_ANALYSIS.ERROR_PARAMS_JSON');
        return;
      }
      if (!this.customCypher?.trim()) {
        this.queryError = this.t('NETWORK_ANALYSIS.ERROR_CYPHER_EMPTY');
        return;
      }
      this.cypher = this.customCypher.trim();
      this.params = parsed;
    }

    // Update query description for display
    this.currentQueryDescription = this.generateQueryDescription();

    // Refresh graph
    this.isLoading = true;
    this.fetchGraphData()
      .then(async ({ nodes, links }) => {
        this.graphData = { nodes, links };
        
        // Ensure graph instance is ready before updating
        await this.ensureGraphInstance();
        
        if (this.graphInstance) {
          // Pause physics while swapping data to avoid jitter
          try { this.graphInstance.pauseAnimation?.(); } catch { }
          // Drop any stale physics positions so the new layout doesn't jump off-screen
          this.resetNodePhysics(this.graphData.nodes);
          this.graphInstance.graphData({ nodes: this.graphData.nodes, links: this.graphData.links });
          this.configureForces();
          // Ensure nodes are not pinned from a previous view
          this.unpinAllNodes();
          // Re-apply center force to container center and then fit
          const container = this.container?.nativeElement as HTMLElement | undefined;
          if (container && (window as any).d3?.forceCenter) {
            this.graphInstance.d3Force('center', (window as any).d3.forceCenter(container.clientWidth / 2, 400));
          }
          // Sanity: if positions explode, reset and try again
          this.safeZoomToFit(800, 60);
          try { this.graphInstance.resumeAnimation?.(); } catch { }
        }
        this.isLoading = false;
        this.closeQueryBuilder();
      })
      .catch((err) => {
        const msg = String(err?.message || err || '');
        console.error('Query error', err);
        if (msg.includes('HARD_LIMIT_EXCEEDED')) {
          const friendly = msg.split('HARD_LIMIT_EXCEEDED:')[1]?.trim() || this.t('NETWORK_ANALYSIS.ERROR_RESULT_TOO_LARGE_DEFAULT');
          this.queryError = friendly;
        } else {
          this.queryError = this.t('NETWORK_ANALYSIS.ERROR_QUERY_FAILED');
        }
        this.isLoading = false;
      });
  }

  onQueryTemplateChange(index: number) {
    const tpl = this.queryTemplates[index];
    if (!tpl) { return; }
    this.customCypher = tpl.cypher;
    try { this.customParamsText = JSON.stringify(tpl.params || {}, null, 2); }
    catch { this.customParamsText = '{}'; }
    this.queryError = '';
  }

  // --- Query Builder helpers ---
  async loadSchemaMeta(): Promise<void> {
    try {
      const driver = this.createNeo4jDriver();
      const session = driver.session();
      try {
        const [labelsRes, relsRes] = await Promise.all([
          this.runCypher(session, 'CALL db.labels()'),
          this.runCypher(session, 'CALL db.relationshipTypes()')
        ]);
        this.availableLabels = (labelsRes.records || [])
          .map((r: any) => r.get(0) || r.get('label'))
          .map((s: any) => String(s))
          .filter(Boolean)
          .sort((a: string, b: string) => a.localeCompare(b));
        this.availableRelTypes = (relsRes.records || [])
          .map((r: any) => r.get(0) || r.get('relationshipType'))
          .map((s: any) => String(s))
          .filter(Boolean)
          .sort((a: string, b: string) => a.localeCompare(b));
      } finally {
        try { await session.close(); } catch { }
        try { await driver.close(); } catch { }
      }
    } catch (e) {
      console.warn('[Neo4j] Failed to load schema metadata (labels/types):', e);
    }
    // Fallback: derive from currently loaded graph if DB metadata unavailable/empty
    if (!this.availableLabels?.length || !this.availableRelTypes?.length) {
      this.deriveSchemaMetaFromGraph();
    }
    // Prefill a default label if empty and we have one
    if (!this.builder.label.label && this.availableLabels?.length) {
      this.builder.label.label = this.availableLabels[0];
    }
    // Prefetch property keys for the current label if set
    if (this.builder.label.label) {
      this.lastPropKeysLabel = this.builder.label.label;
      await this.loadPropertyKeysForLabel(this.builder.label.label).catch(() => { });
    }

    // Ensure async metadata updates render immediately (first-open UX)
    this.cdr.markForCheck();
  }

  private getCurrentGraph(): { nodes: any[]; links: any[] } {
    try {
      const data = this.graphInstance?.graphData?.();
      const nodes = data?.nodes ?? this.graphData.nodes ?? [];
      const links = data?.links ?? this.graphData.links ?? [];
      return { nodes, links };
    } catch {
      return { nodes: this.graphData.nodes ?? [], links: this.graphData.links ?? [] };
    }
  }

  private deriveSchemaMetaFromGraph(): void {
    try {
      const { nodes, links } = this.getCurrentGraph();
      const labelSet = new Set<string>();
      for (const n of nodes || []) {
        const labs: string[] = Array.isArray(n?.labels) ? n.labels : [];
        for (const l of labs) if (l) labelSet.add(String(l));
      }
      const typeSet = new Set<string>();
      for (const l of links || []) {
        const t = l?.type;
        if (t) typeSet.add(String(t));
      }
      const labels = Array.from(labelSet).sort((a, b) => a.localeCompare(b));
      const types = Array.from(typeSet).sort((a, b) => a.localeCompare(b));
      if (labels.length) this.availableLabels = labels;
      if (types.length) this.availableRelTypes = types;
    } catch (e) {
      console.warn('[UI] Failed to derive schema meta from graph:', e);
    }
  }

  // Load property keys for selected label (tries DB, then falls back to current graph)
  async loadPropertyKeysForLabel(label: string): Promise<void> {
    // Always restrict to the fixed set as requested
    this.availableLabelProps = ['Id', 'name', 'Element Id'];
  }

  private derivePropertyKeysFromGraph(label: string): string[] {
    const { nodes } = this.getCurrentGraph();
    const keys = new Set<string>();
    const disallow = new Set(['id', 'labels', 'group', 'x', 'y', 'vx', 'vy', 'fx', 'fy', 'index', '__indexColor', '__bckgDimensions', 'val']);
    for (const n of nodes || []) {
      const labs: string[] = Array.isArray((n as any)?.labels) ? (n as any).labels : [];
      if (!labs.includes(label)) continue;
      for (const k of Object.keys(n || {})) {
        if (!disallow.has(k)) keys.add(k);
      }
    }
    return Array.from(keys).sort((a, b) => a.localeCompare(b));
  }

  onSimpleFieldChange(): void {
    // Update property keys when label changes in Label mode
    if (this.builderMode === 'label') {
      const lbl = (this.builder.label.label || '').trim();
      if (lbl && lbl !== this.lastPropKeysLabel) {
        this.lastPropKeysLabel = lbl;
        this.loadPropertyKeysForLabel(lbl).catch(() => { });
      }
    }
    this.updateBuilderPreview();
  }

  onBuilderTabChange(tab: 'simple' | 'advanced'): void {
    this.builderTab = tab;
    this.currentQueryDescription = this.generateQueryDescription();
  }

  private safeIdent(name: string): string | null {
    return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? name : null;
  }

  private parseRelTypes(text: string): string[] {
    if (!text) return [];
    return text.split(/[|,\s]+/).map(s => s.trim()).filter(Boolean);
  }

  // If a user accidentally splits an underscored type with a pipe/comma (e.g. 'CO|OCCURS_WITH'),
  // merge adjacent tokens when their underscore-joined form matches a known relationship type.
  private mergeTypeTokens(tokens: string[], knownTypes: string[] | null | undefined): string[] {
    if (!Array.isArray(tokens) || tokens.length < 2 || !Array.isArray(knownTypes) || knownTypes.length === 0) return tokens;
    const known = new Set(knownTypes);
    const out: string[] = [];
    for (let i = 0; i < tokens.length; i++) {
      const a = tokens[i];
      const b = tokens[i + 1];
      if (b) {
        const merged = `${a}_${b}`;
        if (known.has(merged)) {
          out.push(merged);
          i++; // skip next
          continue;
        }
      }
      out.push(a);
    }
    return out;
  }

  private buildRelPattern(direction: 'any' | 'out' | 'in', types: string[]): string {
    const typePart = types.length > 0 ? ':' + types.map(t => this.safeIdent(t)).filter(Boolean).join('|') : '';
    if (direction === 'out') return `-[r${typePart}]->`;
    if (direction === 'in') return `<-[r${typePart}]-`;
    return `-[r${typePart}]-`;
  }

  private buildHopPattern(direction: 'any' | 'out' | 'in', hops: number): string {
    const dirPart = this.buildRelPattern(direction, []); // will include [r]
    // Replace [r] with [r*1..hops] keeping direction
    const mult = `*1..${Math.max(1, Math.min(6, Math.floor(hops || 1)))}`;
    return dirPart.replace('[r', `[r${mult}`);
  }

  private buildSimpleCypher(): { ok: boolean; cypher?: string; params?: any; error?: string } {
    switch (this.builderMode) {
      case 'neighborhood': {
        const useName = !!this.builder.neighborhood.useName;
        const id = (this.builder.neighborhood.id || '').trim();
        const name = (this.builder.neighborhood.name || '').trim();
        const hops = Math.max(1, Math.min(6, Math.floor(this.builder.neighborhood.hops || 1)));
        const direction = this.builder.neighborhood.direction;
        const label = (this.builder.neighborhood.entityLabel || '').trim();
        const safeLabel = this.safeLabelIdent(label);
        const matchHead = safeLabel ? `MATCH (n:${safeLabel})` : 'MATCH (n)';

        // Require input
        if (useName ? !name : !id) {
          return { ok: false, error: useName ? this.t('NETWORK_ANALYSIS.ERROR_ENTER_NODE_NAME') : this.t('NETWORK_ANALYSIS.ERROR_ENTER_NODE_ID') };
        }

        let matchLine: string;
        const params: any = {};

        if (useName) {
          // Name mode: support multiple name-like properties per label; also filter by group token when label is unavailable
          const labelWhere = (!safeLabel && this.isGroupToken(label)) ? this.buildLabelWhereForGroup(label) : 'true';
          const coalesceExpr = safeLabel ? this.buildNameExprForLabel(safeLabel, 'n') : `coalesce(n.ProviderName, n.AgentName, n.DoctorName, n.PhysicianName, n.FullName, n.fullName, n.insuredName, n.CustomerName, n.ClientName, n.Name, n.name, trim(coalesce(n.FirstName,'') + ' ' + coalesce(n.LastName,'')), trim(coalesce(n.GivenName,'') + ' ' + coalesce(n.Surname,'')))`;
          matchLine = `${matchHead} WHERE (${labelWhere}) AND toLower(${coalesceExpr}) CONTAINS toLower($name)`;
          params.name = name;

        } else {
          //  STRICT: only pure digits allowed for ID, otherwise return EMPTY RESULT
          if (!/^\d+$/.test(id)) {
            return { ok: true, cypher: 'MATCH (n) WHERE false RETURN n LIMIT 0', params: {} };
          }

          const idNum = Math.floor(Number(id));
          const neoId = (neo4j as any).int ? (neo4j as any).int(idNum) : idNum;
          matchLine = `${matchHead} WHERE id(n) = $id`;
          params.id = neoId;
        }

        if (this.builder.neighborhood.fastMode) {
          const hopPattern = this.buildHopPattern(direction, hops);
          const maxPaths = Math.max(1, Math.floor(this.builder.neighborhood.maxPaths || 200));
          params.maxPaths = maxPaths;

          // Return path arrays for extractor consistency (ensures all entity types render)
          const cypher = `${matchLine}
      MATCH p = (n)${hopPattern}(m)
      WITH p LIMIT $maxPaths
      RETURN nodes(p) AS nodes, relationships(p) AS rels`;

          return { ok: true, cypher, params };

        } else {
          const hopPattern = this.buildHopPattern(direction, hops);
          const maxPaths = Math.max(1, Math.floor(this.builder.neighborhood.maxPaths || 200));
          params.maxPaths = maxPaths;

          const cypher = `${matchLine}
      MATCH p = (n)${hopPattern}(m)
      WITH p LIMIT $maxPaths
      RETURN nodes(p) AS nodes, relationships(p) AS rels`;

          return { ok: true, cypher, params };
        }
      }

      case 'label': {
        const label = (this.builder.label.label || '').trim();
        const safeLabel = this.safeIdent(label);
        if (!safeLabel) return { ok: false, error: this.t('NETWORK_ANALYSIS.ERROR_VALID_LABEL') };
        const keyRaw = (this.builder.label.propKey || '').trim();
        const keyL = keyRaw.toLowerCase();
        const val = this.builder.label.propValue;
        const dir = this.builder.label.direction;
        let types = this.parseRelTypes(this.builder.label.relTypesText).map(t => t).filter(Boolean);
        // Heuristic: merge accidentally split tokens into underscored types if they exist in metadata
        if (types.length && this.availableRelTypes?.length) {
          types = this.mergeTypeTokens(types, this.availableRelTypes).filter(Boolean);
        }
        const pattern = this.buildRelPattern(dir, types);
        const whereParts: string[] = [];
        const params: any = { limit: Math.max(1, Math.floor(this.builder.label.limit || 300)) };
     // If filtering by internal id or elementId, search across all nodes (label-independent)
      const useGlobalMatch = keyL === 'id' || keyL === 'element id' || keyL === 'elementid';
      const matchHead = useGlobalMatch ? 'MATCH (n)' : `MATCH (n:${safeLabel})`;

      if (keyRaw) {
        if (keyL === 'id') {
          // Filter by internal id(n)
          const numVal = Number(val);
          if (!isFinite(numVal)) {
            return { ok: false, error: this.t('NETWORK_ANALYSIS.ERROR_ID_MUST_BE_NUMBER') };
          }
          const n = Math.floor(numVal);
          const neoInt = (neo4j as any).int ? (neo4j as any).int(n) : n;
          whereParts.push('id(n) = $idValue');
          params.idValue = neoInt;

        } else if (keyL === 'element id' || keyL === 'elementid') {
          whereParts.push('elementId(n) = $value');
          params.value = val;

        } else if (keyL === 'name') {
          // Support both 'Name' and 'name' properties; case-insensitive partial match
          whereParts.push('toLower(coalesce(n.Name, n.name, "")) CONTAINS toLower($value)');
          params.value = val;

        } else {
          //  Fallback: return EMPTY result if key is invalid
          const safeKey = this.safeIdent(keyRaw);
          if (safeKey) {
            whereParts.push(`n.${safeKey} = $value`);
            params.value = val;
          } else {
            // User typed something string → return no results
            return {
              ok: true,
              cypher: 'MATCH (n) WHERE false RETURN n LIMIT 0',
              params: {}
            };
          }
        }
      }

      const where = whereParts.length ? `WHERE ${whereParts.join(' AND ')}` : '';
      const cypher = `${matchHead} ${where}
      OPTIONAL MATCH (n)${pattern}(m)
      RETURN n, r, m LIMIT toInteger($limit)`;

      return { ok: true, cypher, params };
      }
      case 'relType': {
        const type = (this.builder.relType.type || '').trim();
        const safeType = this.safeIdent(type);
        if (!safeType) return { ok: false, error: this.t('NETWORK_ANALYSIS.ERROR_VALID_REL_TYPE') };
        const dir = this.builder.relType.direction;
        const limit = Math.max(1, Math.floor(this.builder.relType.limit || 300));
        const minWeight = Math.max(0, Number(this.builder.relType.minWeight || 0));
        const pattern = this.buildRelPattern(dir, [safeType]);
        const where = minWeight > 0 ? 'WHERE coalesce(r.weight,1) >= $minWeight' : '';
        const cypher = `MATCH (a)${pattern}(b) ${where}\nRETURN a, r, b LIMIT toInteger($limit)`;
        const params: any = { limit };
        if (minWeight > 0) params.minWeight = minWeight;
        return { ok: true, cypher, params };
      }
      case 'shortestPath': {
        const useName = !!this.builder.shortestPath.useName;
        const id1 = (this.builder.shortestPath.id1 || '').trim();
        const id2 = (this.builder.shortestPath.id2 || '').trim();
        const name1 = (this.builder.shortestPath.name1 || '').trim();
        const name2 = (this.builder.shortestPath.name2 || '').trim();
        const maxHops = Math.max(1, Math.min(10, Math.floor(this.builder.shortestPath.maxHops || 4)));
        if (useName ? (!name1 || !name2) : (!id1 || !id2)) {
          return { ok: false, error: useName ? this.t('NETWORK_ANALYSIS.ERROR_BOTH_NAMES') : this.t('NETWORK_ANALYSIS.ERROR_BOTH_IDS') };
        }
        let cypher: string;
        const params: any = {};
        if (useName) {
          // Exact name match, supporting both 'Name' and 'name' properties with case-insensitive comparison
          cypher = `MATCH (a) WHERE toLower(coalesce(a.Name, a.name, '')) = toLower($name1) WITH a LIMIT 1\nMATCH (b) WHERE toLower(coalesce(b.Name, b.name, '')) = toLower($name2) WITH a, b LIMIT 1\nMATCH p = shortestPath((a)-[*..${maxHops}]-(b))\nRETURN nodes(p) AS nodes, relationships(p) AS rels`;
          params.name1 = name1; params.name2 = name2;
        } else {
          cypher = `MATCH (a) WHERE id(a) = $id1\nMATCH (b) WHERE id(b) = $id2\nMATCH p = shortestPath((a)-[*..${maxHops}]-(b))\nRETURN nodes(p) AS nodes, relationships(p) AS rels`;
          params.id1 = Math.floor(Number(id1)); params.id2 = Math.floor(Number(id2));
        }
        return { ok: true, cypher, params };
      }
      
      case 'frequency': {
        const useName = !!this.builder.frequency.useNodeName;
        const startNode = (this.builder.frequency.startingNode || '').trim();
        const entityLabel = (this.builder.frequency.entityLabel || '').trim();
        const minFreqWeight = Math.max(0, Number(this.builder.frequency.minFrequencyWeight || 5));
        const maxDepth = Math.max(1, Math.min(4, Math.floor(this.builder.frequency.maxDepth || 2)));
        const topNodesLimit = Math.max(1, Math.floor(this.builder.frequency.topNodesLimit || 100));
        const relType = (this.builder.frequency.relationshipType || 'CO_OCCURS_WITH').trim();
        const weightFilterExpr = this.weightMode === 'cost'
          ? 'coalesce(rel.CostWeight, rel.costWeight, rel.cost_weight, 0)'
          : 'coalesce(rel.FrequencyWeight, rel.frequencyWeight, rel.frequency_weight, rel.weight, 0)';
        const communityProp = this.weightMode === 'cost' ? 'community_COSTWEIGHT' : 'community_FREQUENCYWEIGHT';
        
        if (!startNode) {
          return { ok: false, error: this.t('NETWORK_ANALYSIS.ERROR_ENTER_NODE_NAME') };
        }

        const params: any = { minFreqWeight, maxDepth, topNodesLimit };
        let matchLine: string;

        if (useName) {
          // Name mode: find node by name
          const nameExpr = entityLabel ? this.buildNameExprForLabel(entityLabel, 'startNode') : `coalesce(startNode.ProviderName, startNode.AgentName, startNode.DoctorName, startNode.PhysicianName, startNode.FullName, startNode.fullName, startNode.insuredName, startNode.CustomerName, startNode.ClientName, startNode.Name, startNode.name)`;
          matchLine = `MATCH (startNode) WHERE toLower(${nameExpr}) CONTAINS toLower($startName)`;
          params.startName = startNode;
        } else {
          // ID mode
          if (!/^\d+$/.test(startNode)) {
            return { ok: true, cypher: 'MATCH (n) WHERE false RETURN n LIMIT 0', params: {} };
          }
          const nodeId = Math.floor(Number(startNode));
          const neoId = (neo4j as any).int ? (neo4j as any).int(nodeId) : nodeId;
          matchLine = `MATCH (startNode) WHERE id(startNode) = $startId`;
          params.startId = neoId;
        }

        // Frequency-based network query:
        // 1. Find starting node
        // 2. Walk out $maxDepth steps via CO_OCCURS_WITH (or specified relationship type)
        // 3. Filter by weight >= $minFreqWeight (on relationships)
        // 4. Return top nodes ranked by aggregated weight + centrality metrics (degree, pagerank)
        // 5. Include community detection for clustering
        const cypher = `
${matchLine}
WITH startNode LIMIT 1

// Collect neighbors at varying depths and aggregate their relationship weights
MATCH path = (startNode)-[r:${relType}*1..${maxDepth}]-(neighbor)
WITH DISTINCT neighbor, 
     relationships(path) AS rels,
     coalesce(neighbor.degree, 0) AS nodeDegree,
     coalesce(neighbor.pagerank, 0.0) AS pageRank,
     coalesce(neighbor.${communityProp}, -1) AS freqCommunity
WITH neighbor, nodeDegree, pageRank, freqCommunity,
     [rel IN rels | ${weightFilterExpr}] AS weights
WITH neighbor, nodeDegree, pageRank, freqCommunity,
     reduce(total = 0, w IN weights | total + w) AS totalWeight
WHERE totalWeight >= $minFreqWeight
ORDER BY (totalWeight * 0.6 + nodeDegree * 0.2 + pageRank * 100 * 0.2) DESC
LIMIT toInteger($topNodesLimit)

// Create a subgraph around these high-frequency nodes
WITH COLLECT(DISTINCT neighbor {.*}) AS topNodes, startNode

// Return paths from starting node to top frequency neighbors
UNWIND topNodes AS node
MATCH p = (startNode)-[r:${relType}*1..${maxDepth}]-(node)
WITH p, relationships(p) AS pathRels, nodes(p) AS pathNodes
WHERE any(rel IN pathRels WHERE ${weightFilterExpr} >= $minFreqWeight)

RETURN pathNodes AS nodes, pathRels AS rels
LIMIT ${topNodesLimit}
`;

        return { ok: true, cypher: cypher.trim(), params };
      }
    }
  }

  private generateQueryDescription(): string {
    // If using Query Builder, generate description based on current mode and settings
    if (this.showQueryBuilder && this.builderTab === 'simple') {
      return this.getSimpleBuilderDescription();
    }

    // For custom queries, try to generate a basic description from the Cypher
    return this.getCypherDescription();
  }

  private getSimpleBuilderDescription(): string {
    switch (this.builderMode) {
      case 'neighborhood': {
        const useName = !!this.builder.neighborhood.useName;
        const identifier = useName ? this.builder.neighborhood.name : this.builder.neighborhood.id;
        const hops = this.builder.neighborhood.hops;
        const direction = this.builder.neighborhood.direction;
        const fastMode = this.builder.neighborhood.fastMode;
        
        if (!identifier) return this.t('NETWORK_ANALYSIS.DESC_CONFIG_NEIGHBORHOOD');
        
        let desc = this.t(useName ? 'NETWORK_ANALYSIS.DESC_NEIGHBORHOOD_BY_NAME' : 'NETWORK_ANALYSIS.DESC_NEIGHBORHOOD_BY_ID',{ hops, identifier });
        if (direction === 'out') {
        desc += ' ' + this.t('NETWORK_ANALYSIS.DESC_DIRECTION_OUT_ONLY');
        } else if (direction === 'in') {
          desc += ' ' + this.t('NETWORK_ANALYSIS.DESC_DIRECTION_IN_ONLY');
        }
        if (fastMode) desc += ' ' + this.t('NETWORK_ANALYSIS.DESC_FAST_MODE');
        return desc;
      }

      case 'label': {
        const label = this.builder.label.label;
        const propKey = (this.builder.label.propKey || '').trim();
        const propValue = this.builder.label.propValue;
        const direction = this.builder.label.direction;

        if (!label) return this.t('NETWORK_ANALYSIS.DESC_CONFIGURING_LABEL');

        let filter = '';
        if (propKey && propValue !== '' && propValue !== undefined && propValue !== null) {
          const keyShown = propKey.toLowerCase() === 'id' ? 'ID' : propKey;
          filter = this.t('NETWORK_ANALYSIS.DESC_LABEL_FILTER', {
            propKey: keyShown,
            propValue
          });
        }

        let desc = this.t('NETWORK_ANALYSIS.DESC_LABEL', { label, filter });

        if (direction === 'out') {
          desc += ' ' + this.t('NETWORK_ANALYSIS.DESC_LABEL_OUT_ONLY');
        } else if (direction === 'in') {
          desc += ' ' + this.t('NETWORK_ANALYSIS.DESC_LABEL_IN_ONLY');
        }
        return desc;
      }

      case 'relType': {
        const type = (this.builder.relType.type || '').trim();
        const direction = this.builder.relType.direction;
        const minWeight = this.builder.relType.minWeight;

        if (!type) return this.t('NETWORK_ANALYSIS.DESC_CONFIGURING_REL');

        const dir =
          direction === 'out'
            ? this.t('NETWORK_ANALYSIS.DESC_REL_DIR_OUT')
            : direction === 'in'
            ? this.t('NETWORK_ANALYSIS.DESC_REL_DIR_IN')
            : '';

        const weight = minWeight > 0
          ? this.t('NETWORK_ANALYSIS.DESC_REL_WITH_MIN_WEIGHT', { minWeight })
          : '';

        return this.t('NETWORK_ANALYSIS.DESC_REL', { type, dir, weight });
      }

      case 'shortestPath': {
        const useName = !!this.builder.shortestPath.useName;
        const id1 = useName ? this.builder.shortestPath.name1 : this.builder.shortestPath.id1;
        const id2 = useName ? this.builder.shortestPath.name2 : this.builder.shortestPath.id2;
        const maxHops = this.builder.shortestPath.maxHops;
        
        if (!id1 || !id2) return this.t('NETWORK_ANALYSIS.DESC_CONFIGURING_SHORTEST');
        
        return this.t(useName ? 'NETWORK_ANALYSIS.DESC_SHORTEST_BY_NAME' : 'NETWORK_ANALYSIS.DESC_SHORTEST_BY_ID', { a: id1, b: id2, maxHops });
      }

      case 'frequency': {
        const startNode = this.builder.frequency.startingNode;
        const minFreq = this.builder.frequency.minFrequencyWeight;
        const depth = this.builder.frequency.maxDepth;
        
        if (!startNode) return this.t('NETWORK_ANALYSIS.DESC_CONFIGURING_FREQUENCY');
        
        return this.t('NETWORK_ANALYSIS.DESC_FREQUENCY_ANALYSIS', { startNode, minFreq, depth });
      }

      default:
        return this.t('NETWORK_ANALYSIS.DESC_CUSTOM');
    }
  }

  private getCypherDescription(): string {
    if (!this.cypher) return this.t('NETWORK_ANALYSIS.NO_QUERY');
    
    const cypher = this.cypher.toLowerCase().trim();

    // Simple pattern matching for common query types
    if (cypher.includes('shortestpath')) {
      return this.t('NETWORK_ANALYSIS.DESC_SHORTEST');
    } else if (cypher.includes('match') && cypher.includes('*')) {
      return this.t('NETWORK_ANALYSIS.DESC_VAR_LENGTH');
    } else if (cypher.includes('co_occurs_with') || cypher.includes('co-occurs-with')) {
      return this.t('NETWORK_ANALYSIS.DESC_COOCCURRENCE');
    } else if (cypher.includes('treats') || cypher.includes('diagnosed')) {
      return this.t('NETWORK_ANALYSIS.DESC_MEDICAL');
    } else if (cypher.includes('patient') || cypher.includes('doctor') || cypher.includes('provider')) {
      return this.t('NETWORK_ANALYSIS.DESC_HEALTHCARE');
    } else if (cypher.includes('label')) {
      return this.t('NETWORK_ANALYSIS.DESC_LABELS_GENERIC');
    } else if (cypher.includes('relationship') || cypher.includes('rel')) {
      return this.t('NETWORK_ANALYSIS.DESC_REL_GENERIC');
    } else if (cypher.includes('match') && cypher.includes('return')) {
      return this.t('NETWORK_ANALYSIS.DESC_CUSTOM');
    } else {
      return this.t('NETWORK_ANALYSIS.DESC_DB_QUERY');
    }
  }

  private updateBuilderPreview(): void {
    const built = this.buildSimpleCypher();
    if (built.ok) {
      this.previewCypher = this.inlineCypherParams(built.cypher || '', built.params || {});
      // For easier copy/paste into Neo4j Browser, we inline params into the Cypher preview.
      // Keep params internal for execution.
      this.previewParamsText = '';
      this.queryError = '';
    } else {
      this.previewCypher = '';
      this.previewParamsText = '';
      // Don't force error here; show inline hint in UI instead
    }
    // Update query description
    this.currentQueryDescription = this.generateQueryDescription();
  }

  private debugLog(...args: any[]) {
    if (this.neo4jDebug) {
      try { console.info('[Neo4j]', ...args); } catch { /* no-op */ }
    }
  }

  // Keep references for comparison/export
  lastRawResult: any = null;
  lastExtractedGraph: { nodes: any[]; links: any[] } | null = null;
  private readonly HARD_LIMIT_NODES = 1500;
  private readonly HARD_LIMIT_LINKS = 4000;

  // Current query description for user-friendly display
  currentQueryDescription: string = '';
  noRelationshipsWarning: string = '';

  isLoading = false;
  // Settings drawer visibility
  isSettingsOpen = false;
  isHelpOpen = false;
  selected: any = null;
  private hoverNode: any = null;
  private highlightNodes = new Set();
  private highlightLinks = new Set();


  pinNodesAfterDrag = false;


  showLabels = true;
  showLinkLabels = false;
  showSelectedPanel = true;
  showGraphInfoPanel = false;

  // Labels: only show automatically when node's on-screen radius is big enough
  // Always show for highlighted/selected. This is in screen pixels, zoom-aware.
  private labelMinScreenRadius = 22;


  searchQuery = '';
  searchResults: any[] = [];
  selectedSearchResult: any = null;
  graphData: { nodes: any[], links: any[] } = { nodes: [], links: [] };

  // Main providers populated from Neo4j (fallbacks applied if fetch fails)
  mainProviders: Array<{ id: number; elementId: string; name: string; }> = [];

  private readProvidersCache(): Array<{ id: number; elementId: string; name: string }> | null {
    try {
      const raw = localStorage.getItem(this.providersCacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      const cleaned = parsed
        .map((p: any) => ({
          id: Number(p?.id),
          elementId: String(p?.elementId ?? ''),
          name: String(p?.name ?? '')
        }))
        .filter(p => isFinite(p.id) && !!p.name);
      return cleaned.length ? cleaned : null;
    } catch {
      return null;
    }
  }

  private writeProvidersCache(list: Array<{ id: number; elementId: string; name: string }>): void {
    try {
      localStorage.setItem(this.providersCacheKey, JSON.stringify(list));
    } catch {
      // ignore quota / private mode
    }
  }

  private async fetchAllProviders(): Promise<void> {
    console.log('[NetworkAnalysis] fetchAllProviders: Starting fetch.');
    if (!this.config?.primaryEntityLabel) {
      console.error('[NetworkAnalysis] fetchAllProviders: Aborting, primaryEntityLabel is not configured.');
      return;
    }
    
    const entityLabel = this.config.primaryEntityLabel;
    const nameExpr = this.buildNameExprForLabel(entityLabel, 'n');

    console.log(`[NetworkAnalysis] fetchAllProviders: Fetching entities with label "${entityLabel}".`);

    const driver = this.createNeo4jDriver();
    const session = driver.session();
    try {
      const cypher = `MATCH (n:${entityLabel})\nWITH n, ${nameExpr} AS name\nWHERE name <> ''\nRETURN id(n) as id, elementId(n) as elementId, name as name\nORDER BY name`;
      console.log('[NetworkAnalysis] fetchAllProviders: Executing Cypher query:', cypher);
      
      const result = await this.runCypher(session, cypher);
      console.log(`[NetworkAnalysis] fetchAllProviders: Query returned ${result.records?.length ?? 0} records.`);
      
      const providers = result.records
        .map((record: any) => {
          const idVal = record.get('id');
          const id = (idVal && typeof (idVal as any)?.toNumber === 'function')
            ? (idVal as any).toNumber()
            : Number(idVal);
          return {
            id,
            elementId: String(record.get('elementId') ?? ''),
            name: String(record.get('name') ?? '')
          };
        })
        .filter((p: { id: number; name: string }) => isFinite(p.id) && !!p.name);

      console.log(`[NetworkAnalysis] fetchAllProviders: Processed ${providers.length} valid provider records.`);

      if (providers.length) {
        this.mainProviders = providers;
        this.writeProvidersCache(providers);
        console.log('[NetworkAnalysis] fetchAllProviders: mainProviders array updated. Triggering change detection.');
        this.cdr.markForCheck();
      } else {
        console.warn('[NetworkAnalysis] fetchAllProviders: No providers found or processed. The dropdown will be empty.');
      }
    } catch (err) {
      console.error(`[NetworkAnalysis] fetchAllProviders: Error fetching all ${entityLabel}s:`, err);
    } finally {
      console.log('[NetworkAnalysis] fetchAllProviders: Closing session and driver.');
      await session.close();
      await driver.close();
    }
  }

  // Expose quick stats for template (info window)
  get nodeCount(): number {
    try { return this.graphInstance?.graphData()?.nodes?.length ?? this.graphData?.nodes?.length ?? 0; } catch { return 0; }
  }
  get linkCount(): number {
    try { return this.graphInstance?.graphData()?.links?.length ?? this.graphData?.links?.length ?? 0; } catch { return 0; }
  }
  get highlightedNodeCount(): number { return this.highlightNodes?.size ?? 0; }
  get highlightedLinkCount(): number { return this.highlightLinks?.size ?? 0; }

  // Selected Node essentials
   get selectedNode(): any | null {
    return this.selected && !this.selected.type ? this.selected : null;
  }
  get selectedNodeName(): string {
    return this.formatNodeLabelForDisplay(this.selectedNode?.name ?? '');
  }
  get selectedNodeId(): string {
    return String(this.selectedNode?.id ?? '');
  }
  get selectedNodeActualId(): string {
    const node = this.selectedNode;
    if (!node) return '';

    // Use the numeric ID we preserved during data processing
    if (node.numericId !== null && node.numericId !== undefined) {

      return node.numericId.toString();
    }

    // The numeric ID from Neo4j comes through as identity in the driver
    if (node.identity) {
      if (typeof node.identity === 'object' && 'low' in node.identity) {

        return node.identity.low.toString();
      } else if (typeof node.identity === 'number') {

        return node.identity.toString();
      }
    }

    // Extract from the elementId/id pattern (last number after colon)
    // The id field contains the elementId like "4:d643233f-e110-451c-baeb-f6da2b70a240:2"
    if (node.id && typeof node.id === 'string') {
      const parts = node.id.split(':');
      if (parts.length >= 3) {
        const lastPart = parts[parts.length - 1];
        if (!isNaN(Number(lastPart))) {

          return lastPart;
        }
      }
    }

    // Try elementId if it exists
    if (node.elementId && typeof node.elementId === 'string') {
      const parts = node.elementId.split(':');
      if (parts.length >= 3) {
        const lastPart = parts[parts.length - 1];
        if (!isNaN(Number(lastPart))) {

          return lastPart;
        }
      }
    }

    // Check if there's a numeric 'id' property in the node properties (from Neo4j)
    if (node.hasOwnProperty('id') && typeof node.id === 'number') {

      return node.id.toString();
    }


    return 'N/A';
  }
  get selectedNodeCommunity(): any {
    return this.selectedNode?.community ?? null;
  }
  get selectedNodeVal(): number | null {
    return typeof this.selectedNode?.val === 'number' ? this.selectedNode.val : null;
  }
  get selectedNodeDegree(): number {
    const node = this.selectedNode;
    if (!node) return 0;
    const data = this.graphInstance?.graphData?.();
    const links: any[] = data?.links ?? this.graphData.links ?? [];
    const nid = String(node.id);
    let deg = 0;
    for (const l of links) {
      const srcId = typeof l.source === 'object' ? String(l.source?.id) : String(l.source);
      const tgtId = typeof l.target === 'object' ? String(l.target?.id) : String(l.target);
      if (srcId === nid || tgtId === nid) deg++;
    }
    return deg;
  }

  // Actual database metrics (from Neo4j schema)
  get selectedNodeFrequencyWeight(): number | null {
    const val = this.selectedNode?.FrequencyWeight;
    return typeof val === 'number' ? val : null;
  }

  get selectedNodeCostWeight(): number | null {
    const val = this.selectedNode?.CostWeight;
    return typeof val === 'number' ? val : null;
  }

  get selectedNodeDegreeMetric(): number | null {
    const val = this.selectedNode?.degree;
    return typeof val === 'number' ? val : null;
  }

  get selectedNodePageRank(): number | null {
    const val = this.selectedNode?.pagerank;
    return typeof val === 'number' ? val : null;
  }

  get selectedNodeBetweenness(): number | null {
    const val = this.selectedNode?.betweenness;
    return typeof val === 'number' ? val : null;
  }

  get selectedNodeCloseness(): number | null {
    const val = this.selectedNode?.closeness;
    return typeof val === 'number' ? val : null;
  }

  get selectedNodeEigenvector(): number | null {
    const val = this.selectedNode?.eigenvector;
    return typeof val === 'number' ? val : null;
  }

  get selectedNodeFrequencyCommunity(): number | null {
    const val = this.selectedNode?.community_FREQUENCYWEIGHT;
    return typeof val === 'number' ? val : null;
  }

  get selectedNodeCostCommunity(): number | null {
    const val = this.selectedNode?.community_COSTWEIGHT;
    return typeof val === 'number' ? val : null;
  }

  weightMode: 'frequency' | 'cost' = 'frequency';

  get activeWeightLabelKey(): string {
    return this.weightMode === 'cost'
      ? 'NETWORK_ANALYSIS.COST_WEIGHT'
      : 'NETWORK_ANALYSIS.FREQUENCY_WEIGHT';
  }

  get statusBarNodeLabel(): string | null {
    if (this.selectedProvider?.name) {
      return this.selectedProvider.name;
    }

    if (this.showQueryBuilder && this.builderTab === 'simple') {
      if (this.builderMode === 'neighborhood' && this.builder.neighborhood.useName) {
        const name = (this.builder.neighborhood.name || '').trim();
        return name || null;
      }

      if (this.builderMode === 'frequency' && this.builder.frequency.useNodeName) {
        const name = (this.builder.frequency.startingNode || '').trim();
        return name || null;
      }
    }

    return null;
  }

  get selectedNodeActiveWeight(): number | null {
    return this.weightMode === 'cost'
      ? this.selectedNodeCostWeight
      : this.selectedNodeFrequencyWeight;
  }

  get selectedLinkActiveWeight(): number | null {
    if (!this.selected || !this.selected.type) {
      return null; // Not a link
    }
    const weight = this.getLinkFrequencyWeight(this.selected);
    return typeof weight === 'number' ? weight : null;
  }

  get selectedNodeActiveCommunity(): number | null {
    return this.weightMode === 'cost'
      ? this.selectedNodeCostCommunity
      : this.selectedNodeFrequencyCommunity;
  }

  get selectedNodeAggregateWeight(): number | null {
    if (!this.selectedNode) return null;
    
    const nodeId = this.selectedNode.id;
    const links = this.graphData?.links || [];
    
    // Sum weights of all links connected to this node
    let total = 0;
    let hasWeight = false;
    
    for (const link of links) {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      
      if (sourceId === nodeId || targetId === nodeId) {
        const weight = this.getLinkFrequencyWeight(link);
        if (typeof weight === 'number') {
          total += weight;
          hasWeight = true;
        }
      }
    }
    
    return hasWeight ? total : null;
  }

  // Color mapping for FrequencyWeight visualization
  // Blue (low) → Green (medium) → Red (high)
  private getFrequencyWeightColor(weight: number | undefined): string {
    if (weight === undefined || weight === null) {
      // Default gray for no weight data
      return `rgba(107,114,128,${this.settings.linkOpacity})`;
    }

    let w = weight;
    
    // Handle Neo4j integer objects
    if (typeof weight === 'object' && weight !== null) {
      if ('low' in weight && 'high' in weight) {
        // Neo4j Integer type
        w = (weight as any).high === 0 ? (weight as any).low : Number((weight as any).low);
      } else if ('toNumber' in weight) {
        w = (weight as any).toNumber();
      } else {
        w = Number(weight);
      }
    }

    w = Number(w);
    if (!isFinite(w) || w < 0) {
      return `rgba(107,114,128,${this.settings.linkOpacity})`;
    }

    // Normalize weight to 0-1 range using observed max
    const min = this.minLinkFrequencyWeight ?? 0;
    const max = Math.max(min + 1e-9, this.maxLinkFrequencyWeight || 1);
    let normalized = (w - min) / (max - min);
    normalized = Math.min(1, Math.max(0, normalized));
    // Light gamma to spread lower/mid values
    normalized = Math.sqrt(normalized);

    // Color gradient: blue (0) → cyan (0.25) → green (0.5) → yellow (0.75) → red (1)
    let r = 0, g = 0, b = 0;

    if (normalized < 0.25) {
      // Blue to Cyan
      const t = normalized / 0.25;
      r = 0;
      g = Math.round(255 * t);
      b = 255;
    } else if (normalized < 0.5) {
      // Cyan to Green
      const t = (normalized - 0.25) / 0.25;
      r = 0;
      g = 255;
      b = Math.round(255 * (1 - t));
    } else if (normalized < 0.75) {
      // Green to Yellow
      const t = (normalized - 0.5) / 0.25;
      r = Math.round(255 * t);
      g = 255;
      b = 0;
    } else {
      // Yellow to Red
      const t = (normalized - 0.75) / 0.25;
      r = 255;
      g = Math.round(255 * (1 - t));
      b = 0;
    }

    return `rgba(${r},${g},${b},${this.settings.linkOpacity})`;
  }

  private minLinkFrequencyWeight = 0;
  private maxLinkFrequencyWeight = 1;

  // Filter settings
  hideOrphanPairs: boolean = true;
  largestComponentOnly: boolean = false;
  minLinkWeight: number = 1;
  connectAllPairsPerRecord: boolean = false; // create co-occurrence edges among nodes from the same record
  connectAcrossRecordsUsingDB: boolean = true; // add all DB relationships among loaded nodes (disabled by default for performance)


  settings = {

    nodeRelSize: 12,
    linkWidth: 1,
    linkOpacity: 0.7,
    arrowSize: 6,


    enableParticles: true,
    particleSpeed: 0.005,
    particleWidth: 6,
    particleOpacity: 0.8,
    particleColor: '#FFD700',
    particleDensity: 4,


    linkDistance: 10,
    linkStrength: 0.3,
    chargeStrength: -200,
    chargeDistance: 300,
    centerStrength: 0.01,


    collisionRadius: 2,
    collisionStrength: 0.8
  };

  ngOnInit(): void {
    console.log('[NetworkAnalysis] ngOnInit: Component initializing.');
    console.log('[NetworkAnalysis] ngOnInit: Received config:', this.config);

    this.splashTitle = this.config.splash?.title ?? 'Network Analysis';
    this.splashSubtitle = this.config.splash?.subtitle ?? 'Select a starting point to begin your investigation.';

    this.mainProviders = this.readProvidersCache() || [];
    
    // Initialize graph asynchronously (don't block ngOnInit)
    console.log('[NetworkAnalysis] Starting graph initialization...');
    this.renderEmptyGraph()
      .then(() => {
        console.log('[NetworkAnalysis] Graph instance initialized successfully');
      })
      .catch((error) => {
        console.error('[NetworkAnalysis] Graph initialization FAILED:', error);
      });
    
    this.showSplash();

    if (this.config?.primaryEntityLabel) {
      console.log('[NetworkAnalysis] ngOnInit: Primary entity label found. Fetching all providers.');
      this.fetchAllProviders();
    } else {
      console.warn('[NetworkAnalysis] ngOnInit: No primaryEntityLabel configured.');
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver && this.container) {
      this.resizeObserver.disconnect();
    }


    if (this.keyboardListener) {
      document.removeEventListener('keydown', this.keyboardListener);
    }

    // Ensure body overflow is restored if component is destroyed while drawer open
    if (typeof document !== 'undefined' && this.prevBodyOverflow !== null) {
      document.body.style.overflow = this.prevBodyOverflow;
      this.prevBodyOverflow = null;
    }

    if (this.layoutMainEl && this.prevLayoutMainOverflow !== null) {
      this.layoutMainEl.style.overflow = this.prevLayoutMainOverflow;
      this.prevLayoutMainOverflow = null;
      this.layoutMainEl = null;
    }

    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler);
      this.windowResizeHandler = undefined;
    }

    if(this.graphInstance) {
      this.graphInstance.pauseAnimation?.();

      this.graphInstance.linkDirectionalParticles?.(0);

      this.graphInstance.stopSimulation?.();

      this.graphInstance = null;
    }
  }

  private addKeyboardListeners() {
    this.keyboardListener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {

        this.clearHighlights();
      }
    };
    document.addEventListener('keydown', this.keyboardListener);
  }

  private async fetchGraphData() {
    this.noRelationshipsWarning = '';
    const opStart = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    const driver = this.createNeo4jDriver();
    this.debugLog('Driver created. Verifying connectivity...');
    // Best-effort connectivity check (does not alter behavior on failure)
    try {
      // @ts-ignore types for v5 return ServerInfo
      const info = await driver.verifyConnectivity();
      this.debugLog('Connectivity OK:', { address: (info as any)?.address, version: (info as any)?.version });
    } catch (e) {
      console.warn('[Neo4j] Connectivity verification failed (will still attempt query):', e);
    }

    const session = driver.session();

    try {
      const qStart = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      this.debugLog('Running query:', {
        cypher: this.cypher,
        params: Object.keys(this.params || {}).reduce((acc: any, k) => { acc[k] = this.params[k]; return acc; }, {})
      });
      const result = await this.runCypher(session, this.cypher, this.sanitizeQueryParams(this.params));
      const qEnd = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      this.debugLog('Query completed in ms:', Math.round(qEnd - qStart), 'records:', result.records?.length ?? 0);
      // Serialize raw result for external comparison
      try {
        this.lastRawResult = this.serializeResult(result);
        (window as any).__NA_LAST_RAW_RESULT__ = this.lastRawResult;
        this.debugLog('Serialized raw result available on window.__NA_LAST_RAW_RESULT__');
      } catch (e) {
        console.warn('[Neo4j] Failed to serialize result for export:', e);
      }
      let nodes: any[] = [];
      let links: any[] = [];
      const nodeIds = new Set<string>();




      for (const record of result.records as any[]) {

        const recordNodes: any[] = [];
        let recordWeight: any = null;

        record.keys.forEach((key: string) => {
          const value = record.get(key);
          
          // Log specifically when we encounter the rels array
          if (key === 'rels' && Array.isArray(value)) {
            console.log('[RELS ARRAY FOUND]:', {
              arrayLength: value.length,
              firstItem: value[0],
              firstItemType: value[0]?.type,
              firstItemProps: value[0]?.properties,
              firstItemStart: value[0]?.start,
              firstItemEnd: value[0]?.end
            });
          }

          if (value && typeof value === 'object' && value.identity && value.labels && value.properties) {

            const nodeId = this.extractNodeId(value);

            if (nodeId) {
              let node;
              if (!nodeIds.has(nodeId)) {
                node = {
                  id: nodeId,
                  name: this.extractNodeName(value),
                  ...this.extractProperties(value.properties),
                  labels: value.labels,
                  group: value.labels?.[0] || 'node'
                };

                nodes.push(node);
                nodeIds.add(nodeId);
              } else if (nodeIds.has(nodeId)) {
                node = nodes.find(n => n.id === nodeId);

              }

              if (node) {
                recordNodes.push(node);
              }
            }
          }

          if (String(key).toLowerCase().includes('weight') ||
            (typeof value !== 'object' ||
              (!value?.identity && !value?.type && !value?.start && !value?.end && !Array.isArray(value)))) {

            if (value && typeof value === 'object' && 'low' in value && 'high' in value) {
              recordWeight = value.high === 0 ? value.low : value;
            } else {
              recordWeight = value;
            }

          }

          else if (value && typeof value === 'object' && value.type && (value.start || value.startId) && (value.end || value.endId)) {
            console.log('[Neo4j] ✅ Found relationship:', {
              type: value.type,
              startId: value.start || value.startId,
              endId: value.end || value.endId,
              properties: value.properties,
              constructor: value.constructor?.name
            });

            // Neo4j 5.x uses startId/endId as Integer2 objects instead of start/end node objects
            let startId: string | null = null;
            let endId: string | null = null;
            
            if (value.start) {
              startId = this.extractNodeId(value.start);
            } else if (value.startId) {
              // startId is an Integer2 object, convert to string
              startId = typeof value.startId === 'object' && 'low' in value.startId 
                ? value.startId.low.toString() 
                : value.startId.toString();
            }
            
            if (value.end) {
              endId = this.extractNodeId(value.end);
            } else if (value.endId) {
              // endId is an Integer2 object, convert to string
              endId = typeof value.endId === 'object' && 'low' in value.endId 
                ? value.endId.low.toString() 
                : value.endId.toString();
            }

            // Ensure endpoints exist; create nodes if they were not encountered yet
            if (startId && !nodeIds.has(startId) && value.start?.identity && value.start?.labels && value.start?.properties) {
              nodes.push({
                id: startId,
                name: this.extractNodeName(value.start),
                ...this.extractProperties(value.start.properties),
                labels: value.start.labels,
                group: value.start.labels?.[0] || 'node'
              });
              nodeIds.add(startId);
            }
            if (endId && !nodeIds.has(endId) && value.end?.identity && value.end?.labels && value.end?.properties) {
              nodes.push({
                id: endId,
                name: this.extractNodeName(value.end),
                ...this.extractProperties(value.end.properties),
                labels: value.end.labels,
                group: value.end.labels?.[0] || 'node'
              });
              nodeIds.add(endId);
            }

            if (startId && endId) {
              const linkId = `${startId}-${endId}-${value.type}`;
              if (!links.find(l => l.id === linkId)) {
                const extractedProps = this.extractProperties(value.properties);
                const link = {
                  source: startId,
                  target: endId,
                  type: value.type,
                  id: linkId,
                  ...extractedProps
                };
                links.push(link);
                
                // Log the first 5 extracted relationships with FrequencyWeight
                if (links.length < 5) {
                  console.log('Extracted relationship from record:', {
                    type: value.type,
                    rawProperties: value.properties,
                    extractedLink: link,
                    FrequencyWeight: link.FrequencyWeight
                  });
                }
              }
            }
          }

          else if (Array.isArray(value)) {
            value.forEach(item => {

              if (item && typeof item === 'object' && item.identity && item.labels && item.properties) {
                const nodeId = this.extractNodeId(item);
                if (nodeId && !nodeIds.has(nodeId)) {
                  const node = {
                    id: nodeId,
                    name: this.extractNodeName(item),
                    ...this.extractProperties(item.properties),
                    labels: item.labels,
                    group: item.labels?.[0] || 'node'
                  };
                  nodes.push(node);
                  nodeIds.add(nodeId);
                  recordNodes.push(node);

                } else if (nodeId) {
                  const existingNode = nodes.find(n => n.id === nodeId);
                  if (existingNode) {
                    recordNodes.push(existingNode);
                  }
                }
              }

              else if (item && typeof item === 'object' && item.type && (item.start || item.startId) && (item.end || item.endId)) {

                let startId: string | null = null;
                let endId: string | null = null;

                if (item.start) {
                  startId = this.extractNodeId(item.start);
                } else if (item.startId) {
                  startId = typeof item.startId === 'object' && 'low' in item.startId
                    ? item.startId.low.toString()
                    : item.startId.toString();
                }

                if (item.end) {
                  endId = this.extractNodeId(item.end);
                } else if (item.endId) {
                  endId = typeof item.endId === 'object' && 'low' in item.endId
                    ? item.endId.low.toString()
                    : item.endId.toString();
                }

                // Ensure endpoints exist; create nodes if they were not encountered yet
                if (startId && !nodeIds.has(startId) && item.start?.identity && item.start?.labels && item.start?.properties) {
                  nodes.push({
                    id: startId,
                    name: this.extractNodeName(item.start),
                    ...this.extractProperties(item.start.properties),
                    labels: item.start.labels,
                    group: item.start.labels?.[0] || 'node'
                  });
                  nodeIds.add(startId);
                }
                if (endId && !nodeIds.has(endId) && item.end?.identity && item.end?.labels && item.end?.properties) {
                  nodes.push({
                    id: endId,
                    name: this.extractNodeName(item.end),
                    ...this.extractProperties(item.end.properties),
                    labels: item.end.labels,
                    group: item.end.labels?.[0] || 'node'
                  });
                  nodeIds.add(endId);
                }

                if (startId && endId) {
                  const linkId = `${startId}-${endId}-${item.type}`;
                  if (!links.find(l => l.id === linkId)) {
                    const extractedProps = this.extractProperties(item.properties);
                    const link = {
                      source: startId,
                      target: endId,
                      type: item.type,
                      id: linkId,
                      ...extractedProps
                    };
                    links.push(link);
                    
                    // Log the first 5 extracted relationships with FrequencyWeight
                    if (links.length < 5) {
                      console.log('Extracted relationship from array:', {
                        type: item.type,
                        rawProperties: item.properties,
                        extractedLink: link,
                        FrequencyWeight: link.FrequencyWeight
                      });
                    }
                  }
                }
              }
            });
          }

          else if (value && typeof value === 'object' && value.segments && Array.isArray(value.segments)) {
            value.segments.forEach((segment: any) => {
              const startNode = segment.start;
              const endNode = segment.end;
              const relationship = segment.relationship;

              if (startNode && startNode.identity && startNode.labels && startNode.properties) {
                const startId = this.extractNodeId(startNode);
                if (startId && !nodeIds.has(startId)) {
                  const node = {
                    id: startId,
                    name: this.extractNodeName(startNode),
                    ...this.extractProperties(startNode.properties),
                    labels: startNode.labels,
                    group: startNode.labels?.[0] || 'node'
                  };
                  nodes.push(node);
                  nodeIds.add(startId);
                }
              }

              if (endNode && endNode.identity && endNode.labels && endNode.properties) {
                const endId = this.extractNodeId(endNode);
                if (endId && !nodeIds.has(endId)) {
                  const node = {
                    id: endId,
                    name: this.extractNodeName(endNode),
                    ...this.extractProperties(endNode.properties),
                    labels: endNode.labels,
                    group: endNode.labels?.[0] || 'node'
                  };
                  nodes.push(node);
                  nodeIds.add(endId);
                }
              }

              if (relationship && typeof relationship === 'object' && relationship.type) {
                const startId = this.extractNodeId(startNode ?? relationship.start);
                const endId = this.extractNodeId(endNode ?? relationship.end);

                if (startId && endId) {
                  const linkId = `${startId}-${endId}-${relationship.type}`;
                  if (!links.find(l => l.id === linkId)) {
                    const extractedProps = this.extractProperties(relationship.properties);
                    const link = {
                      source: startId,
                      target: endId,
                      type: relationship.type,
                      id: linkId,
                      ...extractedProps
                    };
                    links.push(link);

                    if (links.length < 5) {
                      console.log('Processing relationship from path segment:', {
                        type: relationship.type,
                        rawProperties: relationship.properties,
                        extractedLink: link,
                        FrequencyWeight: link.FrequencyWeight
                      });
                    }
                  }
                }
              }
            });
          }
        });


        if (this.connectAllPairsPerRecord && recordNodes.length >= 2) {
          // Connect all unique pairs within the record as CO_OCCURS_WITH
          for (let i = 0; i < recordNodes.length; i++) {
            for (let j = i + 1; j < recordNodes.length; j++) {
              const a = recordNodes[i];
              const b = recordNodes[j];
              const linkId = `${a.id}-${b.id}-CO_OCCURS_WITH`;
              const reverseLinkId = `${b.id}-${a.id}-CO_OCCURS_WITH`;
              if (!links.find(l => l.id === linkId || l.id === reverseLinkId)) {
                links.push({
                  source: a.id,
                  target: b.id,
                  type: 'CO_OCCURS_WITH',
                  id: linkId,
                  weight: recordWeight || 1,
                  FrequencyWeight: recordWeight || 1
                });
              }
            }
          }
        } else if (recordNodes.length === 2) {
          const sourceNode = recordNodes[0];
          const targetNode = recordNodes[1];
          const linkId = `${sourceNode.id}-${targetNode.id}-CO_OCCURS_WITH`;
          const reverseLinkId = `${targetNode.id}-${sourceNode.id}-CO_OCCURS_WITH`;


          if (!links.find(l => l.id === linkId || l.id === reverseLinkId)) {
            const link = {
              source: sourceNode.id,
              target: targetNode.id,
              type: 'CO_OCCURS_WITH',
              id: linkId,
              weight: recordWeight || 1,
              FrequencyWeight: recordWeight || 1
            };
            links.push(link);

          } else {

          }
        } else if (recordNodes.length > 2) {

        } else if (recordNodes.length === 1) {

        } else if (recordNodes.length === 0) {

        }



        if (recordNodes.length > 0) {

        }
      }






      if (links.length === 0) {
        console.warn('WARNING: No relationships were found! This might indicate:');
        console.warn('1. The query does not return relationships directly');
        console.warn('2. Relationships are in a different format than expected');
        console.warn('3. The query only returns nodes without their connections');
        this.noRelationshipsWarning = this.t('NETWORK_ANALYSIS.WARNING_NO_RELATIONSHIPS');
        this.cdr.markForCheck();



        if (result.records.length > 0) {
          result.records[0].keys.forEach((key: string) => {
            const value = result.records[0].get(key);












          });
        }
      }


      // Node cap removed; no truncation warning.
      // Optionally connect across records using DB relationships among loaded nodes
      if (this.connectAcrossRecordsUsingDB && nodes.length > 1) {
        try {
          const idList = nodes.map(n => String(n.id));
          const relStart = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
          const relQuery = `
            UNWIND $ids AS id1
            MATCH (a)-[r:CO_OCCURS_WITH]->(b)
            WHERE elementId(a) = id1 AND elementId(b) IN $ids
            RETURN elementId(a) AS aId,
                   elementId(b) AS bId,
                   type(r) AS type,
                   coalesce(r.FrequencyWeight, r.frequencyWeight, r.frequency_weight, r.weight) AS FrequencyWeight,
                   coalesce(r.CostWeight, r.costWeight, r.cost_weight) AS CostWeight
          `;
          const relRes = await this.runCypher(session, relQuery, { ids: idList });
          const added: number = (relRes.records || []).reduce((acc: number, rec: any) => {
            const aId = String(rec.get('aId'));
            const bId = String(rec.get('bId'));
            const type = String(rec.get('type'));
            const w = rec.get('FrequencyWeight');
            const c = rec.get('CostWeight');
            const linkId = `${aId}-${bId}-${type}`;
            const exists = links.find(l => l.id === linkId);
            if (!exists) {
              const weightValue = (w && typeof w === 'object' && 'low' in w && 'high' in w) ? (w.high === 0 ? w.low : w) : (w ?? 1);
              const costValue = (c && typeof c === 'object' && 'low' in c && 'high' in c) ? (c.high === 0 ? c.low : c) : (c ?? undefined);
              links.push({
                source: aId,
                target: bId,
                type,
                id: linkId,
                weight: weightValue,
                FrequencyWeight: weightValue,
                CostWeight: costValue
              });
              return acc + 1;
            }
            return acc;
          }, 0);
          const relEnd = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
          this.debugLog('Added DB relationships among loaded nodes:', { added, ms: Math.round(relEnd - relStart) });
        } catch (e) {
          console.warn('[Neo4j] Failed to connect across records using DB relationships:', e);
        }
      }

      const filtered = this.applyGraphFilters({ nodes, links });
      nodes = filtered.nodes;
      links = filtered.links;

      const weights = links
        .map(link => this.getLinkFrequencyWeight(link))
        .filter((v): v is number => typeof v === 'number' && isFinite(v))
        .sort((a, b) => a - b);
      if (weights.length > 0) {
        const p05 = weights[Math.floor((weights.length - 1) * 0.05)];
        const p95 = weights[Math.floor((weights.length - 1) * 0.95)];
        const min = Math.min(...weights);
        const max = Math.max(...weights);
        this.minLinkFrequencyWeight = isFinite(p05) ? p05 : min;
        this.maxLinkFrequencyWeight = isFinite(p95) ? p95 : max;
        if (this.maxLinkFrequencyWeight <= this.minLinkFrequencyWeight) {
          this.minLinkFrequencyWeight = min;
          this.maxLinkFrequencyWeight = max || 1;
        }
        console.log('[LinkWeight Range]', {
          count: weights.length,
          min,
          p05: this.minLinkFrequencyWeight,
          p95: this.maxLinkFrequencyWeight,
          max
        });
      } else {
        this.minLinkFrequencyWeight = 0;
        this.maxLinkFrequencyWeight = 1;
      }

      // Size nodes based on connectivity in the *current* graph.
      this.normalizeNodeSizes(nodes, links);
      this.lastExtractedGraph = { nodes: [...nodes], links: [...links] };
      (window as any).__NA_LAST_GRAPH__ = this.lastExtractedGraph;
      this.debugLog('Extracted graph available on window.__NA_LAST_GRAPH__');

      const opEnd = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      this.debugLog('Extraction complete in ms:', Math.round(opEnd - opStart), {
        nodeCount: nodes.length,
        linkCount: links.length
      });
      if (nodes.length > this.HARD_LIMIT_NODES || links.length > this.HARD_LIMIT_LINKS) {
        throw new Error(`HARD_LIMIT_EXCEEDED: Result too large (${nodes.length} nodes / ${links.length} links). Reduce limits or hops and try again.`);
      }
      return { nodes, links };
    } finally {
      const closeStart = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      try { await session.close(); } catch (e) { console.warn('[Neo4j] Session close failed:', e); }
      try { await driver.close(); } catch (e) { console.warn('[Neo4j] Driver close failed:', e); }
      const closeEnd = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      this.debugLog('Resources closed in ms:', Math.round(closeEnd - closeStart));
    }
  }

  // --- Serialization helpers for comparison/export ---
  private serializeResult(result: any): any {
    const out: any = {
      summary: {
        keys: result.records?.[0]?.keys ?? [],
        recordCount: result.records?.length ?? 0
      },
      records: [] as any[]
    };
    const max = Math.min(200, result.records?.length ?? 0);
    for (let i = 0; i < max; i++) {
      const rec = result.records[i];
      const obj: any = {};
      (rec.keys || []).forEach((k: string) => {
        try {
          obj[k] = this.serializeValue(rec.get(k));
        } catch (e) {
          obj[k] = { __error: 'serialize_failed' };
        }
      });
      out.records.push(obj);
    }
    return out;
  }

  private isNeoInt(v: any): boolean {
    return v && typeof v === 'object' && typeof v.low === 'number' && typeof v.high === 'number' && Object.keys(v).length === 2;
  }

  private toSafeNumber(v: any): number | { low: number; high: number } {
    try {
      const asNum = (neo4j as any).integer.inSafeRange(v) ? (neo4j as any).integer.toNumber(v) : null;
      if (asNum !== null) return asNum;
    } catch { }
    return { low: v?.low, high: v?.high };
  }

  // Coerce numeric params used in LIMIT or counts to integers (Neo4j Int)
  private sanitizeQueryParams(params: any): any {
    if (!params || typeof params !== 'object') return params;
    const out: any = Array.isArray(params) ? [] : {};
    const intKeys = new Set(['limit', 'maxPaths', 'maxNodes', 'maxRels', 'minWeight', 'id', 'id1', 'id2', 'idValue']);
    for (const [k, v] of Object.entries(params)) {
      if (v == null) { out[k] = v; continue; }
      if (intKeys.has(k)) {
        const n = Math.floor(Number(v));
        out[k] = (neo4j as any).int ? (neo4j as any).int(n) : n;
      } else {
        out[k] = v;
      }
    }
    return out;
  }

  private serializeValue(value: any): any {
    if (value == null) return value;
    // Neo4j Integer
    if (this.isNeoInt(value)) return this.toSafeNumber(value);
    // Arrays
    if (Array.isArray(value)) return value.map(v => this.serializeValue(v));
    // Node
    if (value && value.identity && value.labels && value.properties) {
      return {
        identity: this.toSafeNumber(value.identity),
        labels: [...(value.labels || [])],
        properties: this.serializeValue(value.properties)
      };
    }
    // Relationship
    if (value && value.identity && value.type && value.start && value.end) {
      return {
        identity: this.toSafeNumber(value.identity),
        type: value.type,
        start: this.serializeValue(value.start),
        end: this.serializeValue(value.end),
        properties: this.serializeValue(value.properties)
      };
    }
    // Path
    if (value && value.segments && Array.isArray(value.segments)) {
      return {
        segments: value.segments.map((seg: any) => ({
          start: this.serializeValue(seg.start),
          relationship: this.serializeValue(seg.relationship),
          end: this.serializeValue(seg.end)
        }))
      };
    }
    // Plain object
    if (typeof value === 'object') {
      const obj: any = {};
      for (const k of Object.keys(value)) obj[k] = this.serializeValue(value[k]);
      return obj;
    }
    return value;
  }

  downloadLastRawResult(): void {
    if (!this.lastRawResult) {
      console.warn('[Neo4j] No raw result captured yet.');
      return;
    }
    const blob = new Blob([JSON.stringify(this.lastRawResult, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `network-analysis-result-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  downloadLastGraph(): void {
    if (!this.lastExtractedGraph) {
      console.warn('[Neo4j] No extracted graph captured yet.');
      return;
    }
    const blob = new Blob([JSON.stringify(this.lastExtractedGraph, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `network-analysis-graph-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // --- Graph filtering ---
  private applyGraphFilters(graph: { nodes: any[]; links: any[] }): { nodes: any[]; links: any[] } {
    if (!graph) return { nodes: [], links: [] };
    let nodes = [...(graph.nodes || [])];
    let links = [...(graph.links || [])];

    // 1) Min weight filter
    const minW = Number(this.minLinkWeight) || 0;
    if (minW > 0) {
      links = links.filter(l => (typeof l.weight === 'number' ? l.weight : 1) >= minW);
    }

    // Build adjacency and node lookup
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    const adjacency = new Map<string, Set<string>>();
    const linkPairs = new Map<string, number>(); // key: a|b sorted, value: count
    for (const l of links) {
      const a = String(l.source);
      const b = String(l.target);
      if (!adjacency.has(a)) adjacency.set(a, new Set());
      if (!adjacency.has(b)) adjacency.set(b, new Set());
      adjacency.get(a)!.add(b);
      adjacency.get(b)!.add(a);
      const key = a < b ? `${a}|${b}` : `${b}|${a}`;
      linkPairs.set(key, (linkPairs.get(key) || 0) + 1);
    }

    // Remove nodes with no adjacency (after weight filter)
    // BUT: if the original query was nodes-only (no links), keep all nodes
    const nodesWithDegree = new Set<string>();
    adjacency.forEach((neighbors, id) => { if (neighbors.size > 0) nodesWithDegree.add(id); });

    // If we have no links at all, this might be a nodes-only query - don't filter out nodes
    if ((links.length !== 0 && graph.links.length !== 0)) {
      // Normal filtering for queries that should have relationships
      nodes = nodes.filter(n => nodesWithDegree.has(String(n.id)) || adjacency.has(String(n.id)));
      // Filter links to match remaining nodes
      links = links.filter(l => {
        const allowedIds = new Set(nodes.map(n => String(n.id)));
        return allowedIds.has(String(l.source)) && allowedIds.has(String(l.target));
      });
    }

    // Create allowedIds set for connected components logic
    const allowedIds = new Set(nodes.map(n => String(n.id)));

    // 2) Connected components
    const visited = new Set<string>();
    const components: string[][] = [];
    for (const id of allowedIds) {
      if (visited.has(id)) continue;
      const comp: string[] = [];
      const q: string[] = [id];
      visited.add(id);
      while (q.length) {
        const cur = q.shift()!;
        comp.push(cur);
        const nbrs = adjacency.get(cur) || new Set();
        for (const nb of nbrs) {
          if (!visited.has(nb) && allowedIds.has(nb)) { visited.add(nb); q.push(nb); }
        }
      }
      components.push(comp);
    }

    // 3) Optionally keep only the largest connected component
    if (this.largestComponentOnly && components.length > 0) {
      let largest = components[0];
      for (const c of components) if (c.length > largest.length) largest = c;
      const keep = new Set(largest);
      nodes = nodes.filter(n => keep.has(String(n.id)));
      links = links.filter(l => keep.has(String(l.source)) && keep.has(String(l.target)));
    }

    // 4) Optionally remove 2-node, single-edge components (orphan pairs)
    if (this.hideOrphanPairs) {
      const ids = new Set(nodes.map(n => String(n.id)));
      // recompute components on the current graph
      const adj2 = new Map<string, Set<string>>();
      for (const l of links) {
        const a = String(l.source), b = String(l.target);
        if (!adj2.has(a)) adj2.set(a, new Set());
        if (!adj2.has(b)) adj2.set(b, new Set());
        adj2.get(a)!.add(b);
        adj2.get(b)!.add(a);
      }
      const seen = new Set<string>();
      const removeIds = new Set<string>();
      const removeLinkKeys = new Set<string>();
      for (const id of ids) {
        if (seen.has(id)) continue;
        const comp: string[] = [];
        const q = [id];
        seen.add(id);
        while (q.length) {
          const cur = q.shift()!;
          comp.push(cur);
          const nbrs = adj2.get(cur) || new Set();
          for (const nb of nbrs) if (!seen.has(nb)) { seen.add(nb); q.push(nb); }
        }
        if (comp.length === 2) {
          const a = comp[0], b = comp[1];
          const key = a < b ? `${a}|${b}` : `${b}|${a}`;
          const edgeCount = linkPairs.get(key) || 0;
          if (edgeCount === 1) {
            removeIds.add(a); removeIds.add(b); removeLinkKeys.add(key);
          }
        }
      }
      if (removeIds.size > 0) {
        nodes = nodes.filter(n => !removeIds.has(String(n.id)));
        links = links.filter(l => {
          const a = String(l.source), b = String(l.target);
          const key = a < b ? `${a}|${b}` : `${b}|${a}`;
          return !removeLinkKeys.has(key);
        });
      }
    }

    return { nodes, links };
  }

  updateGraphWithFilters(): void {
    if (!this.graphInstance || !this.lastExtractedGraph) return;
    const filtered = this.applyGraphFilters(this.lastExtractedGraph);
    this.normalizeNodeSizes(filtered.nodes, filtered.links);
    this.graphInstance.graphData({ nodes: filtered.nodes, links: filtered.links });
    this.forceRedraw();
    // Keep camera consistent with current content
    this.safeZoomToFit(600, 40);
  }

  // Download just the records array for Browser-style JSON comparison
  downloadBrowserStyleRecords(): void {
    const records = this.lastRawResult?.records;
    if (!records || !Array.isArray(records)) {
      console.warn('[Neo4j] No records available to export. Run a query first.');
      return;
    }
    const blob = new Blob([JSON.stringify(records, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `network-analysis-records-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }


  private async renderWith2DForceGraph(container: HTMLElement, nodes: any[], links: any[]) {
    console.log('[NetworkAnalysis] renderWith2DForceGraph started. Container:', container, 'Nodes:', nodes.length, 'Links:', links.length);
    try {
      console.log('[NetworkAnalysis] Importing force-graph library...');
      const ForceGraph = await import('force-graph');
      console.log('[NetworkAnalysis] force-graph imported successfully');

      const graph = new (ForceGraph.default as any)(container)
        .width(container.clientWidth)
        .height(800)
        .cooldownTicks(100)
        .nodeId('id')
        .nodeVal('val')
        .nodeRelSize(this.settings.nodeRelSize)

        .nodeLabel(null)
        .linkLabel(null)
        .onNodeHover(null)
        .onLinkHover(null)

        .nodeCanvasObject((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
          const rawLabel = node.name || this.t('NETWORK_ANALYSIS.UNKNOWN');
          const label = this.formatNodeLabelForDisplay(rawLabel);
          const fontSize = 12 / globalScale;
          const nodeRadius = Math.sqrt(node.val || 1) * this.settings.nodeRelSize;

          // Add a safety check for valid canvas context and positions
          if (!ctx || isNaN(node.x) || isNaN(node.y) || nodeRadius <= 0) {
            if (Math.random() < 0.01) {
              console.warn('[Neo4j] Invalid node rendering parameters:', {
                hasCtx: !!ctx,
                x: node.x,
                y: node.y,
                nodeRadius,
                val: node.val,
                relSize: this.settings.nodeRelSize
              });
            }
            return;
          }


          const isHighlighted = this.highlightNodes.has(node);
          const isDimmed = this.highlightNodes.size > 0 && !isHighlighted;

          if (isDimmed) {
            ctx.globalAlpha = 0.2;
          }

          if (isHighlighted) {
            ctx.save();
            ctx.strokeStyle = 'rgba(255, 215, 0, 0.5)';
            ctx.lineWidth = 6 / globalScale;
            ctx.beginPath();
            ctx.arc(node.x, node.y, nodeRadius + 6, 0, 2 * Math.PI, false);
            ctx.stroke();
            ctx.restore();
          } else {
            ctx.globalAlpha = 1.0;
          }

          // Set the node color
          ctx.fillStyle = this.getNodeColor(node);
          
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
          ctx.fill();


          ctx.globalAlpha = 1.0;


          if (this.shouldRenderLabel(node, nodeRadius, globalScale, isHighlighted)) {

            const words = label.split(' ');
            let lines: string[] = [];

            if (words.length > 1) {

              const midPoint = Math.ceil(words.length / 2);
              lines.push(words.slice(0, midPoint).join(' '));
              if (midPoint < words.length) {
                lines.push(words.slice(midPoint).join(' '));
              }
            } else {

              lines = [label];
            }


            ctx.font = `${fontSize}px Sans-Serif`;
            let maxWidth = 0;
            lines.forEach(line => {
              const lineWidth = ctx.measureText(line).width;
              maxWidth = Math.max(maxWidth, lineWidth);
            });

            const lineHeight = fontSize * 1.2;
            const totalHeight = lines.length * lineHeight;
            const bckgDimensions = [maxWidth + fontSize * 0.4, totalHeight + fontSize * 0.2];


            ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            ctx.fillRect(
              node.x - bckgDimensions[0] / 2,
              node.y - bckgDimensions[1] / 2,
              bckgDimensions[0],
              bckgDimensions[1]
            );


            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#111827';


            const startY = node.y - (totalHeight / 2) + (lineHeight / 2);

            lines.forEach((line, index) => {
              const yPos = startY + (index * lineHeight);
              ctx.fillText(line, node.x, yPos);
            });


            node.__bckgDimensions = bckgDimensions;
          } else {

            node.__bckgDimensions = null;
          }
        })
        .linkSource('source')
        .linkTarget('target')
        .linkWidth(this.settings.linkWidth)
        .linkColor((link: any) => {
          const freqWeight = this.getLinkFrequencyWeight(link);
          const color = this.getFrequencyWeightColor(freqWeight);
          // Log first few links to verify FrequencyWeight is present
          if (Math.random() < 0.05) {
            console.log('Link color mapping:', { FrequencyWeight: freqWeight, color });
          }
          return color;
        })
        .linkDirectionalArrowLength(this.settings.arrowSize)
        .linkDirectionalArrowRelPos(0.75)
        .linkDirectionalArrowColor(() => 'rgba(107,114,128,0.6)')

        .linkDirectionalParticles((link: any) => {

          const isHighlighted = this.highlightLinks.has(link);
          return (this.settings.enableParticles && isHighlighted) ? this.settings.particleDensity : 0;
        })
        .linkDirectionalParticleSpeed(this.settings.particleSpeed)
        .linkDirectionalParticleWidth(this.settings.particleWidth)
        .linkDirectionalParticleColor((link: any) => {

          const isHighlighted = this.highlightLinks.has(link);
          return isHighlighted ? '#FFD700' : this.settings.particleColor;
        })

        .linkCanvasObject((link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {

          const isHighlighted = this.highlightLinks.has(link);
          const isDimmed = this.highlightLinks.size > 0 && !isHighlighted;


          const start = link.source;
          const end = link.target;


          if (isDimmed) {
            ctx.globalAlpha = 0.3;
          } else if (isHighlighted) {
            ctx.globalAlpha = 1.0;
          } else {
            ctx.globalAlpha = this.settings.linkOpacity;
          }


          if (isHighlighted) {

            ctx.strokeStyle = 'rgba(255, 215, 0, 0.3)';
            ctx.lineWidth = this.settings.linkWidth * 4;
            ctx.beginPath();
            ctx.moveTo(start.x, start.y);
            ctx.lineTo(end.x, end.y);
            ctx.stroke();


            ctx.strokeStyle = '#FFD700';
            ctx.lineWidth = this.settings.linkWidth * 2;
          } else {
            const color = this.getFrequencyWeightColor(this.getLinkFrequencyWeight(link));
            ctx.strokeStyle = color;
            ctx.lineWidth = this.settings.linkWidth;

            if (Math.random() < 0.01) {
              console.log('Link canvas color:', { FrequencyWeight: this.getLinkFrequencyWeight(link), color });
            }
          }

          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(end.x, end.y);
          ctx.stroke();


          const arrowLength = this.settings.arrowSize;
          const arrowAngle = Math.atan2(end.y - start.y, end.x - start.x);
          const arrowPos = 0.75; // 75% along the line
          const tipX = start.x + (end.x - start.x) * arrowPos;
          const tipY = start.y + (end.y - start.y) * arrowPos;
          const arrowX = tipX - Math.cos(arrowAngle) * arrowLength;
          const arrowY = tipY - Math.sin(arrowAngle) * arrowLength;

          ctx.fillStyle = isHighlighted ? '#eab308' : 'rgba(107,114,128,0.6)';
          ctx.beginPath();
          ctx.moveTo(tipX, tipY);
          ctx.lineTo(
            arrowX - Math.cos(arrowAngle - Math.PI / 6) * arrowLength,
            arrowY - Math.sin(arrowAngle - Math.PI / 6) * arrowLength
          );
          ctx.lineTo(
            arrowX - Math.cos(arrowAngle + Math.PI / 6) * arrowLength,
            arrowY - Math.sin(arrowAngle + Math.PI / 6) * arrowLength
          );
          ctx.closePath();
          ctx.fill();


          ctx.globalAlpha = 1.0;


          if (this.showLinkLabels && link.type) {

            const midX = (start.x + end.x) / 2;
            const midY = (start.y + end.y) / 2;


            const linkAngle = Math.atan2(end.y - start.y, end.x - start.x);


            const labelText = link.type || this.t('NETWORK_ANALYSIS.LINK_GENERIC');
            const fontSize = 10 / globalScale;
            ctx.font = `${fontSize}px Sans-Serif`;


            const words = labelText.replace(/_/g, ' ').split(' ');
            let lines: string[] = [];

            if (words.length > 1) {

              const midPoint = Math.ceil(words.length / 2);
              lines.push(words.slice(0, midPoint).join(' '));
              if (midPoint < words.length) {
                lines.push(words.slice(midPoint).join(' '));
              }
            } else {

              lines = [labelText];
            }


            let maxWidth = 0;
            lines.forEach(line => {
              const lineWidth = ctx.measureText(line).width;
              maxWidth = Math.max(maxWidth, lineWidth);
            });

            const lineHeight = fontSize * 1.2;
            const totalHeight = lines.length * lineHeight;
            const bckgDimensions = [maxWidth + fontSize * 0.4, totalHeight + fontSize * 0.2];


            ctx.save();


            ctx.translate(midX, midY);


            let rotation = linkAngle;
            if (Math.abs(linkAngle) > Math.PI / 2) {
              rotation = linkAngle + Math.PI;
            }
            ctx.rotate(rotation);


            ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            ctx.fillRect(
              -bckgDimensions[0] / 2,
              -bckgDimensions[1] / 2,
              bckgDimensions[0],
              bckgDimensions[1]
            );


            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#111827';


            const startY = -(totalHeight / 2) + (lineHeight / 2);

            lines.forEach((line, index) => {
              const yPos = startY + (index * lineHeight);
              ctx.fillText(line, 0, yPos);
            });


            ctx.restore();
          }
        })
        .onNodeClick((node: any) => {

          this.selected = node;
          this.updateHighlight(node);
        })
        .onLinkClick((link: any) => {

          this.selected = link;
          this.updateHighlight(link);
        })
        .onBackgroundClick(() => {

          this.selected = null;
          this.updateHighlight(null);
        })
        .onBackgroundRightClick(() => {


          this.selected = null;
          this.updateHighlight(null);
        })

        .onNodeDragEnd((node: any) => {
          if (this.pinNodesAfterDrag) {

            node.fx = node.x;
            node.fy = node.y;

          } else {

            node.fx = undefined;
            node.fy = undefined;
          }
        });


      graph.graphData({ nodes, links });


      const simulation = graph.d3Force('charge');
      if (simulation) {
        simulation.strength(this.settings.chargeStrength);
      }

      const linkForce = graph.d3Force('link');
      if (linkForce) {
        linkForce
          .distance((link: any) => this.calculateLinkDistance(link))
          .strength(this.settings.linkStrength);
      }


      graph.d3Force('center', (window as any).d3?.forceCenter && (window as any).d3.forceCenter(container.clientWidth / 2, 400));
      if (graph.d3Force('center')) {
        graph.d3Force('center').strength(this.settings.centerStrength);
      }


      if ((window as any).d3?.forceCollide) {
        const collisionForce = (window as any).d3.forceCollide()
          .radius((node: any) => {
            const nodeRadius = Math.sqrt(node.val || 1) * this.settings.collisionRadius;
            return nodeRadius + 3;
          })
          .strength(this.settings.collisionStrength)
          .iterations(2);

        graph.d3Force('collision', collisionForce);
      }


      this.resizeObserver = new ResizeObserver(() => {
        graph.width(container.clientWidth);
        graph.d3Force('center', (window as any).d3?.forceCenter && (window as any).d3.forceCenter(container.clientWidth / 2, 400));
      });
      this.resizeObserver.observe(container);


      setTimeout(() => {
        if (graph && graph.zoomToFit) {
          graph.zoomToFit(800, 50);
        }
        this.isLoading = false;
      }, 1000);

      this.graphInstance = graph;
      console.log('[NetworkAnalysis] Graph instance assigned. graphInstance now exists:', !!this.graphInstance);
    } catch (error) {
      console.error('Error with 2D Force Graph:', error);
      this.isLoading = false;
      throw error; // Re-throw so the promise rejects
    }
  }

  private getNodeColor(node: any): string {
    if (!this.config?.actors) {
      return '#a78bfa'; // Default purple for unknown
    }
    
    const label = (node.group || '').toLowerCase();
    
    // Find matching actor by label or aliases
    const actor = this.config.actors.find(a => {
      if (a.label.toLowerCase() === label) return true;
      return a.aliases.some(alias => alias.toLowerCase() === label);
    });
    
    return actor?.color || '#a78bfa'; // Default purple for unknown
  }

  private isCoreactOrCoassessConfig(): boolean {
    const prefix = this.config?.cacheKeyPrefix || '';
    const normalized = prefix.toLowerCase();
    return normalized.startsWith('coreact.') || normalized.startsWith('coassess.');
  }

  private isStagingHost(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const host = (window.location.hostname || '').toLowerCase();
    return (
      host === 'stg.coreact.gr' ||
      host.endsWith('.stg.coreact.gr') ||
      host === 'stg.coassess.gr' ||
      host.endsWith('.stg.coassess.gr')
    );
  }

  private shouldTrimNodeDisplayName(): boolean {
    return this.isCoreactOrCoassessConfig() && this.isStagingHost();
  }

  private formatNodeLabelForDisplay(label: string): string {
    const safeLabel = String(label || '').trim();
    if (!safeLabel) {
      return this.t('NETWORK_ANALYSIS.UNKNOWN');
    }

    if (!this.shouldTrimNodeDisplayName()) {
      return safeLabel;
    }

    const firstName = safeLabel.split(/\s+/)[0] || safeLabel;
    return firstName.substring(0, 4);
  }

  // Decide if a label should be rendered for a node at current scale (zoom-aware, consistent for all)
  private shouldRenderLabel(node: any, nodeRadius: number, globalScale: number, isHighlighted: boolean): boolean {
    if (!this.showLabels) return false; // master toggle
    // Always show for highlighted or explicitly selected
    if (isHighlighted || this.selected === node) return true;
    // Use screen-space radius for consistency across node types and zoom levels
    const screenRadius = nodeRadius * globalScale;
    return screenRadius >= this.labelMinScreenRadius;
  }


  private extractGraphEntities(value: any, nodes: any[], links: any[], nodeIds: Set<string>) {
    if (!value) return;

    if (Array.isArray(value)) {
      // Log array processing - relationships likely coming through here as array
      if (Math.random() < 0.05) {
        console.log('Processing array:', {
          length: value.length,
          firstItem: value[0],
          firstItemKeys: value[0] ? Object.keys(value[0]) : 'N/A'
        });
      }
      value.forEach(v => this.extractGraphEntities(v, nodes, links, nodeIds));
    } else if (value && typeof value === 'object') {

      if (value.segments && Array.isArray(value.segments)) {

        value.segments.forEach((segment: any) => {



          const startId = this.extractNodeId(segment.start);
          if (startId && !nodeIds.has(startId)) {
            nodes.push({
              id: startId,
              name: this.extractNodeName(segment.start),
              ...this.extractProperties(segment.start.properties),
              labels: segment.start.labels,
              group: segment.start.labels?.[0] || 'node'
            });
            nodeIds.add(startId);
          }


          const endId = this.extractNodeId(segment.end);
          if (endId && !nodeIds.has(endId)) {
            nodes.push({
              id: endId,
              name: this.extractNodeName(segment.end),
              ...this.extractProperties(segment.end.properties),
              labels: segment.end.labels,
              group: segment.end.labels?.[0] || 'node'
            });
            nodeIds.add(endId);
          }


          if (segment.relationship && startId && endId) {
            const link = {
              source: startId,
              target: endId,
              type: segment.relationship.type,
              id: this.extractNodeId(segment.relationship),
              ...this.extractProperties(segment.relationship.properties)
            };
            links.push(link);
            
            // Log relationship properties for debugging
            if (Math.random() < 0.1) {
              console.log('Extracted relationship:', {
                type: segment.relationship.type,
                properties: segment.relationship.properties,
                extractedLink: link
              });
            }
          }
        });
      }

      else if (value.identity && value.labels && value.properties) {
        const id = this.extractNodeId(value);
        if (id && !nodeIds.has(id)) {
          nodes.push({
            id,
            name: this.extractNodeName(value),
            ...this.extractProperties(value.properties),
            labels: value.labels,
            group: value.labels?.[0] || 'node'
          });
          nodeIds.add(id);
        }
      }

      else if (value.type && value.start && value.end) {

        const startId = this.extractNodeId(value.start);
        const endId = this.extractNodeId(value.end);

        if (startId && endId) {
          const link = {
            source: startId,
            target: endId,
            type: value.type,
            id: this.extractNodeId(value),
            ...this.extractProperties(value.properties)
          };
          links.push(link);
          
          // Log relationship properties - THIS PATH LIKELY BEING USED
          if (Math.random() < 0.1) {
            console.log('Extracted relationship (early else-if - LIKELY USED):', {
              type: value.type,
              rawProperties: value.properties,
              extractedLink: link
            });
          }
        }
      }

      else {


        if (value.labels && value.properties && value.identity) {
          // Debug: Log raw Neo4j node data
          console.log('Raw Neo4j node data:', {
            elementId: value.elementId,
            identity: value.identity,
            properties: value.properties,
            labels: value.labels
          });

          const id = this.extractNodeId(value);
          const numericId = this.extractNumericIdentity(value.identity);

          console.log('Extracted IDs:', {
            extractedId: id,
            extractedNumericId: numericId,
            identityType: typeof value.identity,
            identityValue: value.identity
          });

          if (id && !nodeIds.has(id)) {
            const nodeData = {
              id,
              name: this.extractNodeName(value),
              ...this.extractProperties(value.properties),
              labels: value.labels,
              group: value.labels?.[0] || 'node',
              elementId: value.elementId, // Preserve elementId
              identity: value.identity,   // Preserve original identity for display
              numericId: numericId        // Extract numeric ID separately
            };


            nodes.push(nodeData);
            nodeIds.add(id);

          }
        }

        else if (value.type && value.start && value.end) {
          const startId = this.extractNodeId(value.start);
          const endId = this.extractNodeId(value.end);

          if (startId && endId) {
            const link = {
              source: startId,
              target: endId,
              type: value.type,
              id: this.extractNodeId(value),
              ...this.extractProperties(value.properties)
            };
            links.push(link);
            
            // Log relationship properties
            if (Math.random() < 0.1) {
              console.log('Extracted relationship (else-if path):', {
                type: value.type,
                rawProperties: value.properties,
                extractedLink: link
              });
            }
          }
        }
      }
    }
  }


  private extractNodeId(value: any): string | null {
    // Prefer elementId (string) to match Neo4j Browser behavior
    if (value?.elementId) return String(value.elementId);
    if (!value?.identity) return null;
    return typeof value.identity === 'object' && 'low' in value.identity
      ? value.identity.low.toString()
      : value.identity.toString();
  }

  private extractNumericIdentity(identity: any): number | null {


    if (!identity) {

      return null;
    }

    if (typeof identity === 'number') {

      return identity;
    }

    if (typeof identity === 'object' && 'low' in identity) {

      return identity.low;
    }


    return null;
  }

  private extractNodeName(value: any): string {
    const props = value.properties || {};
    return props.name || props.Name || props.title || props.id || this.t('NETWORK_ANALYSIS.UNNAMED');
  }

  private extractProperties(properties: any): any {
    if (!properties) return {};
    const result: any = {};
    for (const [key, value] of Object.entries(properties)) {
      if (value && typeof value === 'object' && 'low' in value && 'high' in value) {
        result[key] = value.high === 0 ? value.low : value;
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  private getLinkFrequencyWeight(link: any): number | undefined {
    const raw = this.weightMode === 'cost'
      ? (link?.CostWeight
        ?? link?.costWeight
        ?? link?.properties?.CostWeight)
      : (link?.FrequencyWeight
        ?? link?.frequencyWeight
        ?? link?.weight
        ?? link?.Weight
        ?? link?.properties?.FrequencyWeight
        ?? link?.properties?.weight);

    if (raw == null) return undefined;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    if (typeof raw === 'object' && 'low' in raw && 'high' in raw) {
      return (raw as any).high === 0 ? (raw as any).low : (raw as any);
    }
    return undefined;
  }

  private updateWeightRange(links: any[]) {
    const weights = links
      .map(link => this.getLinkFrequencyWeight(link))
      .filter((v): v is number => typeof v === 'number' && isFinite(v))
      .sort((a, b) => a - b);
    if (weights.length > 0) {
      const p05 = weights[Math.floor((weights.length - 1) * 0.05)];
      const p95 = weights[Math.floor((weights.length - 1) * 0.95)];
      const min = Math.min(...weights);
      const max = Math.max(...weights);
      this.minLinkFrequencyWeight = isFinite(p05) ? p05 : min;
      this.maxLinkFrequencyWeight = isFinite(p95) ? p95 : max;
      if (this.maxLinkFrequencyWeight <= this.minLinkFrequencyWeight) {
        this.minLinkFrequencyWeight = min;
        this.maxLinkFrequencyWeight = max || 1;
      }
      console.log('[LinkWeight Range]', {
        mode: this.weightMode,
        count: weights.length,
        min,
        p05: this.minLinkFrequencyWeight,
        p95: this.maxLinkFrequencyWeight,
        max
      });
    } else {
      this.minLinkFrequencyWeight = 0;
      this.maxLinkFrequencyWeight = 1;
    }
  }

  private normalizeNodeSizes(nodes: any[], linksOverride?: any[], animate = false) {
    if (nodes.length === 0) return;

    const links = linksOverride
      ?? this.graphData?.links
      ?? this.graphInstance?.graphData?.()?.links
      ?? [];

    const nodeIds = new Set(nodes.map(n => String(n.id)));
    const degreeMap = new Map<string, number>();
    const weightMap = new Map<string, number>();

    const endpointId = (v: any): string | null => {
      if (v == null) return null;
      if (typeof v === 'string' || typeof v === 'number') return String(v);
      if (typeof v === 'object') {
        if ('id' in v && v.id != null) return String((v as any).id);
        if ('elementId' in v && v.elementId != null) return String((v as any).elementId);
        if ('identity' in v && (v as any).identity != null) {
          const id = (v as any).identity;
          if (typeof id === 'number') return String(id);
          if (typeof id === 'object' && 'low' in id) return String((id as any).low);
          try { return String(id); } catch { }
        }
      }
      return null;
    };

    // Count connections for each node (only among the provided nodes)
    (links as any[]).forEach((link: any) => {
      const sourceId = endpointId(link?.source);
      const targetId = endpointId(link?.target);
      if (!sourceId || !targetId) return;
      if (!nodeIds.has(sourceId) || !nodeIds.has(targetId)) return;
      degreeMap.set(sourceId, (degreeMap.get(sourceId) || 0) + 1);
      degreeMap.set(targetId, (degreeMap.get(targetId) || 0) + 1);

      const weight = this.getLinkFrequencyWeight(link);
      if (typeof weight === 'number' && isFinite(weight)) {
        weightMap.set(sourceId, (weightMap.get(sourceId) || 0) + weight);
        weightMap.set(targetId, (weightMap.get(targetId) || 0) + weight);
      }
    });

    // Include 0-degree nodes in normalization so isolated nodes stay small.
    const degrees = nodes.map(n => degreeMap.get(String(n.id)) || 0);
    const minDegree = degrees.length ? Math.min(...degrees) : 0;
    const maxDegree = degrees.length ? Math.max(...degrees) : 0;

    const weights = nodes.map(n => weightMap.get(String(n.id)) || 0);
    const minWeight = weights.length ? Math.min(...weights) : 0;
    const maxWeight = weights.length ? Math.max(...weights) : 0;

    nodes.forEach(n => {
      const nodeType = this.determineNodeType(n);
      const degree = degreeMap.get(String(n.id)) || 0;
      const aggWeight = weightMap.get(String(n.id)) || 0;
      
      // Base size by node type
      let baseSize = 1;
      switch (nodeType) {
        case 'doctor': baseSize = 1.5; break;
        case 'provider': baseSize = 1.8; break;
        case 'customer_code': baseSize = 0.8; break;
        case 'customer': baseSize = 0.8; break;
        case 'patient': baseSize = 0.8; break;
        case 'agent': baseSize = 1.2; break;
        case 'agent_name': baseSize = 1.2; break;
      }

      // Scale size by degree (connections): make high-degree hubs stand out.
      // Wider range + curve: small nodes stay small, big hubs get much bigger.
      const minMult = 0.35;
      const maxMult = 6.0;
      const curve = 0.65; // < 1 boosts high values

      const normalizedMetric = this.weightMode === 'cost'
        ? ((maxWeight > minWeight) ? (aggWeight - minWeight) / (maxWeight - minWeight) : 0)
        : ((maxDegree > minDegree) ? (degree - minDegree) / (maxDegree - minDegree) : 0);

      const sizeMultiplier = minMult + (Math.pow(normalizedMetric, curve) * (maxMult - minMult));
      
      const targetVal = baseSize * sizeMultiplier;
      if (!animate || typeof n.val !== 'number') {
        n.val = targetVal;
      }
      (n as any).__targetVal = targetVal;
      n.degree = degree; // Store for tooltip/info
      n.group = nodeType;
    });

    if (animate) {
      this.animateNodeSizeChange(nodes);
    }
  }

  private nodeSizeAnimationFrame: number | null = null;

  private animateNodeSizeChange(nodes: any[], duration = 350): void {
    if (!nodes || nodes.length === 0) return;

    if (this.nodeSizeAnimationFrame !== null) {
      try { cancelAnimationFrame(this.nodeSizeAnimationFrame); } catch { }
      this.nodeSizeAnimationFrame = null;
    }

    const start = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    const startVals = new Map<any, number>();
    const targetVals = new Map<any, number>();

    nodes.forEach(n => {
      const startVal = (typeof n.val === 'number') ? n.val : 1;
      const targetVal = (typeof (n as any).__targetVal === 'number') ? (n as any).__targetVal : startVal;
      startVals.set(n, startVal);
      targetVals.set(n, targetVal);
    });

    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; // easeInOutQuad

      nodes.forEach(n => {
        const from = startVals.get(n) ?? 1;
        const to = targetVals.get(n) ?? from;
        n.val = from + (to - from) * eased;
      });

      if (this.graphInstance?.refresh) {
        this.graphInstance.refresh();
      } else if (this.graphInstance) {
        const currentNodeRelSize = this.settings.nodeRelSize;
        this.graphInstance.nodeRelSize(currentNodeRelSize + 0.0001);
        this.graphInstance.nodeRelSize(currentNodeRelSize);
      }

      if (t < 1) {
        this.nodeSizeAnimationFrame = requestAnimationFrame(tick);
      } else {
        nodes.forEach(n => { delete (n as any).__targetVal; });
        this.nodeSizeAnimationFrame = null;
      }
    };

    this.nodeSizeAnimationFrame = requestAnimationFrame(tick);
  }

  // Remove physics/camera fields so a new dataset starts fresh
  private resetNodePhysics(nodes: any[]): void {
    try {
      nodes?.forEach(n => {
        delete (n as any).x; delete (n as any).y;
        delete (n as any).vx; delete (n as any).vy;
        delete (n as any).fx; delete (n as any).fy;
        delete (n as any).index;
      });
    } catch { }
  }

  private positionsAreValid(nodes: any[]): boolean {
    if (!nodes || nodes.length === 0) return true;
    for (const n of nodes) {
      const x = (n as any).x, y = (n as any).y;
      if (x == null || y == null) continue; // not initialized yet
      if (!isFinite(x) || !isFinite(y)) return false;
      if (Math.abs(x) > 1e7 || Math.abs(y) > 1e7) return false;
    }
    return true;
  }

  private sanitizePositions(nodes: any[], cx = 0, cy = 0): void {
    try {
      nodes?.forEach(n => {
        const x = (n as any).x, y = (n as any).y;
        if (!isFinite(x) || !isFinite(y) || Math.abs(x) > 1e7 || Math.abs(y) > 1e7) {
          (n as any).x = cx + (Math.random() - 0.5) * 50;
          (n as any).y = cy + (Math.random() - 0.5) * 50;
          (n as any).vx = 0; (n as any).vy = 0;
        }
      });
    } catch { }
  }

  private safeZoomToFit(ms = 600, padding = 50): void {

    try {
      if (!this.graphInstance) {

        return;
      }
      const container = this.container?.nativeElement as HTMLElement | undefined;
      const cx = container ? container.clientWidth / 2 : 0;
      const cy = 400;


      const data = this.graphInstance.graphData?.();
      const nodes: any[] = data?.nodes || [];


      if (!this.positionsAreValid(nodes)) {

        this.sanitizePositions(nodes, cx, cy);
      }
      // Defer to next frame to let simulation accept updated positions
      const bounds = this.computeBounds(nodes);


      const useManual = !bounds.valid || bounds.w > 1e6 || bounds.h > 1e6 || bounds.w === 0 || bounds.h === 0;


      requestAnimationFrame(() => {
        try {
          if (useManual) {

            this.resetCamera();
            this.graphInstance.centerAt(bounds.cx, bounds.cy, ms);
            this.graphInstance.zoom(1.2, ms);
          } else {

            this.graphInstance?.zoomToFit?.(ms, padding);
          }
        } catch { }
      });
    } catch { }
  }

  private computeBounds(nodes: any[]): { valid: boolean; minX: number; minY: number; maxX: number; maxY: number; w: number; h: number; cx: number; cy: number } {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let count = 0, sumX = 0, sumY = 0;
    for (const n of nodes || []) {
      const x = (n as any).x, y = (n as any).y;
      if (!isFinite(x) || !isFinite(y)) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      sumX += x; sumY += y; count++;
    }
    const valid = count > 0 && isFinite(minX) && isFinite(minY) && isFinite(maxX) && isFinite(maxY);
    const w = valid ? Math.max(1, maxX - minX) : 0;
    const h = valid ? Math.max(1, maxY - minY) : 0;
    const cx = valid ? sumX / count : 0;
    const cy = valid ? sumY / count : 0;
    return { valid, minX, minY, maxX, maxY, w, h, cx, cy };
  }

  private resetCamera(): void {
    try {
      this.graphInstance.centerAt(0, 0, 0);
      this.graphInstance.zoom(1, 0);
    } catch { }
  }

  private determineNodeType(node: any): string {
    if (node.labels?.[0]) {
      const label = node.labels[0].toLowerCase();
      if (label.includes('doctor')) return 'doctor';
      if (label.includes('provider')) return 'provider';
      if (label.includes('customer')) return 'customer_code';
      if (label.includes('patient')) return 'patient';
      return label;
    }
    return 'node';
  }


  get filteredSelected() {
    if (!this.selected) return null;
    const filtered = { ...this.selected };
    delete filtered.x;
    delete filtered.y;
    delete filtered.vx;
    delete filtered.vy;
    delete filtered.fx;
    delete filtered.fy;
    delete filtered.index;
    delete filtered.__indexColor;
    delete (filtered as any).__bckgDimensions;
    // Hide technical identifiers and structural refs from the info box
    delete (filtered as any).id;
    delete (filtered as any).elementId;
    delete (filtered as any).labels;
    delete (filtered as any).group;
    delete (filtered as any).source;
    delete (filtered as any).target;
    return filtered;
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj || {});
  }

  formatValue(value: any): string {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    return String(value);
  }

  isDisplayable(value: any): boolean {
    return (
      value === null ||
      ['string', 'number', 'boolean'].includes(typeof value)
    );
  }


  zoomIn() {
    if (this.graphInstance && this.graphInstance.zoom) {
      this.graphInstance.zoom(this.graphInstance.zoom() * 1.5, 500);
    }
  }

  zoomOut() {
    if (this.graphInstance && this.graphInstance.zoom) {
      this.graphInstance.zoom(this.graphInstance.zoom() / 1.5, 500);
    }
  }


  unpinAllNodes() {
    if (this.graphInstance) {
      const graphData = this.graphInstance.graphData();
      graphData.nodes.forEach((node: any) => {
        node.fx = undefined;
        node.fy = undefined;
      });

    }
  }


  resetZoom() {
    if (this.graphInstance && this.graphInstance.zoomToFit) {
      this.graphInstance.zoomToFit(800, 50);

      this.unpinAllNodes();
    }
  }


  updateNodeSize() {
    if (this.graphInstance) {
      this.graphInstance.nodeRelSize(this.settings.nodeRelSize);
      this.restartSimulation();
    }
  }

  updateLinkWidth() {
    if (this.graphInstance) {
      this.graphInstance.linkWidth(this.settings.linkWidth);
      this.restartSimulation();
    }
  }

  updateLinkOpacity() {
    if (this.graphInstance) {
      this.graphInstance.linkColor(() => `rgba(0,0,0,${this.settings.linkOpacity})`);
    }
  }

  updateArrowSize() {
    if (this.graphInstance) {
      this.graphInstance.linkDirectionalArrowLength(this.settings.arrowSize);
      this.restartSimulation();
    }
  }


  updateParticleEnabled() {
    if (this.graphInstance) {
      this.graphInstance.linkDirectionalParticles((link: any) => {
        const isHighlighted = this.highlightLinks.has(link);
        return (this.settings.enableParticles && isHighlighted) ? this.settings.particleDensity : 0;
      });
      this.restartSimulation();
    }
  }

  updateParticleSpeed() {
    if (this.graphInstance) {
      this.graphInstance.linkDirectionalParticleSpeed(this.settings.particleSpeed);
    }
  }

  updateParticleWidth() {
    if (this.graphInstance) {
      this.graphInstance.linkDirectionalParticleWidth(this.settings.particleWidth);
    }
  }

  updateParticleDensity() {
    if (this.graphInstance) {
      this.graphInstance.linkDirectionalParticles((link: any) => {
        const isHighlighted = this.highlightLinks.has(link);
        return (this.settings.enableParticles && isHighlighted) ? this.settings.particleDensity : 0;
      });
      this.restartSimulation();
    }
  }

  calculateLinkDistance(link: any): number {
    const weight = this.getLinkFrequencyWeight(link);
    const baseDistance = this.settings.linkDistance;

    const sizeMultiplier = this.getLinkSizeMultiplier(link);
    
    if (weight === undefined || weight === 0) {
      return baseDistance * 3 * sizeMultiplier; // No weight = far apart
    }
    
    // Inverse relationship: higher weight = shorter distance
    // Use normalized weight (0-1 range) to scale distance
    const normalizedWeight = this.minLinkFrequencyWeight === this.maxLinkFrequencyWeight
      ? 0.5
      : (weight - this.minLinkFrequencyWeight) / (this.maxLinkFrequencyWeight - this.minLinkFrequencyWeight);
    
    // High weight (1.0) = baseDistance * 0.5 (close together)
    // Low weight (0.0) = baseDistance * 2.5 (far apart)
    const distanceMultiplier = 2.5 - (normalizedWeight * 2.0);
    return baseDistance * distanceMultiplier * sizeMultiplier;
  }

  private getLinkSizeMultiplier(link: any): number {
    const radius = (node: any): number => {
      if (!node || typeof node !== 'object') return this.settings.nodeRelSize;
      const val = (node as any).val;
      const base = this.settings.nodeRelSize;
      return Math.sqrt(typeof val === 'number' && isFinite(val) ? val : 1) * base;
    };

    const source = (link as any)?.source;
    const target = (link as any)?.target;
    const r1 = radius(source);
    const r2 = radius(target);
    const avgRadius = (r1 + r2) / 2;

    // Scale distance up slightly for larger nodes to avoid overlap.
    // Cap the multiplier so spacing doesn’t explode.
    const baseRadius = this.settings.nodeRelSize;
    const ratio = baseRadius > 0 ? avgRadius / (baseRadius * 2) : 1; // typical range ~0.5–2
    return 1 + Math.min(2.0, Math.max(0, ratio * 1.5));
  }

  updateLinkDistance() {
    if (this.graphInstance) {
      const forceLink = this.graphInstance.d3Force('link');
      if (forceLink) {
        forceLink.distance((link: any) => this.calculateLinkDistance(link));
        this.restartSimulation();
      }
    }
  }

  updateLinkStrength() {
    if (this.graphInstance) {
      const forceLink = this.graphInstance.d3Force('link');
      if (forceLink) {
        forceLink.strength(this.settings.linkStrength);
        this.restartSimulation();
      }
    }
  }

  updateChargeStrength() {
    if (this.graphInstance) {
      const forceCharge = this.graphInstance.d3Force('charge');
      if (forceCharge) {
        forceCharge.strength(this.settings.chargeStrength);
        this.restartSimulation();
      }
    }
  }

  updateChargeDistance() {
    if (this.graphInstance) {
      const forceCharge = this.graphInstance.d3Force('charge');
      if (forceCharge) {
        forceCharge.distanceMax(this.settings.chargeDistance);
        this.restartSimulation();
      }
    }
  }

  updateCenterStrength() {
    if (this.graphInstance) {
      const forceCenter = this.graphInstance.d3Force('center');
      if (forceCenter) {
        forceCenter.strength(this.settings.centerStrength);
        this.restartSimulation();
      }
    }
  }

  updateCollisionRadius() {
    if (this.graphInstance) {
      const forceCollide = this.graphInstance.d3Force('collision');
      if (forceCollide) {
        forceCollide.radius((node: any) => {
          const baseSize = Math.sqrt(node.val || 4);
          return baseSize * this.settings.collisionRadius + 5;
        });
        this.restartSimulation();
      }
    }
  }

  updateCollisionStrength() {
    if (this.graphInstance) {
      const forceCollide = this.graphInstance.d3Force('collision');
      if (forceCollide) {
        forceCollide.strength(this.settings.collisionStrength);
        this.restartSimulation();
      }
    }
  }


  private restartSimulation() {
    if (this.graphInstance && this.graphInstance.d3ReheatSimulation) {
      this.graphInstance.d3ReheatSimulation();
    }
  }


  applyPreset(preset: string) {
    switch (preset) {
      case 'tight':
        this.settings = {
          ...this.settings,
          linkDistance: 30,
          linkStrength: 0.8,
          chargeStrength: -50,
          chargeDistance: 100,
          collisionRadius: 1,
          collisionStrength: 1
        };
        break;

      case 'spread':
        this.settings = {
          ...this.settings,
          linkDistance: 120,
          linkStrength: 0.1,
          chargeStrength: -400,
          chargeDistance: 400,
          collisionRadius: 4,
          collisionStrength: 0.5
        };
        break;

      case 'hub':
        this.settings = {
          ...this.settings,
          linkDistance: 80,
          linkStrength: 0.5,
          chargeStrength: -100,
          chargeDistance: 200,
          collisionRadius: 2,
          collisionStrength: 0.8
        };
        break; case 'default':
        this.settings = {
          nodeRelSize: 12,
          linkWidth: 1,
          linkOpacity: 0.7,
          arrowSize: 6,


          enableParticles: true,
          particleSpeed: 0.005,
          particleWidth: 6,
          particleOpacity: 0.8,
          particleColor: '#FFD700',
          particleDensity: 4,

          linkDistance: 10,
          linkStrength: 0.3,
          chargeStrength: -200,
          chargeDistance: 300,
          centerStrength: 0.01,
          collisionRadius: 2,
          collisionStrength: 0.8
        };
        break;
    }


    this.updateAllSettings();
  }

  private updateAllSettings() {
    this.updateNodeSize();
    this.updateLinkWidth();
    this.updateLinkOpacity();
    this.updateArrowSize();
    this.updateParticleEnabled();
    this.updateParticleSpeed();
    this.updateParticleWidth();
    this.updateParticleDensity();
    this.updateLinkDistance();
    this.updateLinkStrength();
    this.updateChargeStrength();
    this.updateChargeDistance();
    this.updateCenterStrength();
    this.updateCollisionRadius();
    this.updateCollisionStrength();

    this.restartSimulation();
  }

  // Toggle hook for pair-connection behavior
  async onConnectPairsChange() {
    // Rebuild graph data from last raw result if needed, else re-filter
    await this.ensureGraphInstance();
    if (this.graphInstance) {
      // Quick approach: re-fetch using the same cypher to rebuild links according to the new toggle
      this.fetchGraphData().then(({ nodes, links }) => {
        this.graphData = { nodes, links };
        this.graphInstance.graphData({ nodes, links });
        this.forceRedraw();
      });
    }
  }

  onWeightModeChange(mode: 'frequency' | 'cost') {
    this.weightMode = mode;
    if (this.builderTab === 'simple' && this.builderMode === 'frequency') {
      const built = this.buildSimpleCypher();
      if (built.ok && built.cypher) {
        this.cypher = built.cypher.trim();
        this.params = built.params || {};
        this.currentQueryDescription = this.generateQueryDescription();
        this.isLoading = true;
        this.fetchGraphData()
          .then(async ({ nodes, links }) => {
            this.graphData = { nodes, links };
            await this.ensureGraphInstance();
            this.graphInstance?.graphData({ nodes, links });
            if (nodes.length) {
              this.normalizeNodeSizes(nodes, links, true);
            }
            this.updateLinkDistance();
            this.forceRedraw();
            this.isLoading = false;
          })
          .catch(() => {
            this.isLoading = false;
          });
        return;
      }
    }

    const links = this.graphInstance?.graphData?.()?.links ?? this.graphData?.links ?? [];
    const nodes = this.graphInstance?.graphData?.()?.nodes ?? this.graphData?.nodes ?? [];
    this.updateWeightRange(links);
    if (nodes.length) {
      this.normalizeNodeSizes(nodes, links, true);
    }
    this.updateLinkDistance();
    this.forceRedraw();
  }

  // Drawer controls
  openSettings() {
    this.isSettingsOpen = true;
    // Prevent double scrollbars: lock page scroll while drawer is open
    if (typeof document !== 'undefined') {
      this.prevBodyOverflow = document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';

      // Also lock the app shell container if present
      this.layoutMainEl = document.querySelector('.layout-main') as HTMLElement | null;
      if (this.layoutMainEl) {
        this.prevLayoutMainOverflow = this.layoutMainEl.style.overflow || '';
        this.layoutMainEl.style.overflow = 'hidden';
      }
    }
  }
  closeSettings() {
    this.isSettingsOpen = false;
    if (typeof document !== 'undefined' && !this.isHelpOpen) {
      document.body.style.overflow = this.prevBodyOverflow ?? '';
      this.prevBodyOverflow = null;

      if (this.layoutMainEl) {
        this.layoutMainEl.style.overflow = this.prevLayoutMainOverflow ?? '';
        this.prevLayoutMainOverflow = null;
        this.layoutMainEl = null;
      }
    }
  }
  toggleSettings() { this.isSettingsOpen ? this.closeSettings() : this.openSettings(); }

  openHelp() {
    this.isHelpOpen = true;
    if (typeof document !== 'undefined' && !this.isSettingsOpen) {
      this.prevBodyOverflow = document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';

      this.layoutMainEl = document.querySelector('.layout-main') as HTMLElement | null;
      if (this.layoutMainEl) {
        this.prevLayoutMainOverflow = this.layoutMainEl.style.overflow || '';
        this.layoutMainEl.style.overflow = 'hidden';
      }
    }
  }

  closeHelp() {
    this.isHelpOpen = false;
    if (typeof document !== 'undefined' && !this.isSettingsOpen) {
      document.body.style.overflow = this.prevBodyOverflow ?? '';
      this.prevBodyOverflow = null;

      if (this.layoutMainEl) {
        this.layoutMainEl.style.overflow = this.prevLayoutMainOverflow ?? '';
        this.prevLayoutMainOverflow = null;
        this.layoutMainEl = null;
      }
    }
  }

  toggleHelp() {
    if (this.isHelpOpen) {
      this.closeHelp();
    } else {
      this.closeSettings();
      this.openHelp();
    }
  }

  toggleInfoPanel() {
    this.showGraphInfoPanel = !this.showGraphInfoPanel;
  }

  toggleDownloadDropdown() {
    this.showDownloadDropdown = !this.showDownloadDropdown;
  }


  onPinNodesChange() {

    if (!this.pinNodesAfterDrag) {

      this.unpinAllNodes();

    }
  }


  onShowLabelsChange() {
    // Force immediate redraw so label visibility updates without waiting for physics
    this.forceRedraw();
  }


  onShowLinkLabelsChange() {


    if (this.graphInstance) {
      this.restartSimulation();
    }
  }


  updateHighlight(obj: any) {



    this.highlightNodes.clear();
    this.highlightLinks.clear();

    if (obj) {
      if (obj.id) {


        this.hoverNode = obj;
        this.highlightNodes.add(obj);


        if (this.graphInstance) {
          const graphData = this.graphInstance.graphData();
          graphData.links.forEach((link: any) => {
            if (link.source.id === obj.id || link.target.id === obj.id) {
              this.highlightLinks.add(link);

              this.highlightNodes.add(link.source);
              this.highlightNodes.add(link.target);
            }
          });
        }


      } else if (obj.source && obj.target) {


        this.highlightLinks.add(obj);
        this.highlightNodes.add(obj.source);
        this.highlightNodes.add(obj.target);
      }
    } else {


      this.hoverNode = null;
    }


    this.forceRedraw();
  }


  private forceRedraw() {
    if (!this.graphInstance) return;


    this.graphInstance.linkDirectionalParticles((link: any) => {
      const isHighlighted = this.highlightLinks.has(link);
      return (this.settings.enableParticles && isHighlighted) ? this.settings.particleDensity : 0;
    });


    if (typeof this.graphInstance.refresh === 'function') {
      this.graphInstance.refresh();
      return;
    }


    const currentNodeRelSize = this.settings.nodeRelSize;
    this.graphInstance.nodeRelSize(currentNodeRelSize + 0.001);
    setTimeout(() => {
      if (this.graphInstance) {
        this.graphInstance.nodeRelSize(currentNodeRelSize);
      }
    }, 1);
  }


  isHighlightActive(): boolean {
    return this.highlightNodes.size > 0 || this.highlightLinks.size > 0;
  }

  getHighlightStatus(): string {
    const n = this.highlightNodes.size;
    const l = this.highlightLinks.size;

    if (n > 0 && l > 0) {
      const nLabel = n === 1 ? this.t('NETWORK_ANALYSIS.NODE_SINGULAR') : this.t('NETWORK_ANALYSIS.NODES_PLURAL');
      const lLabel = l === 1 ? this.t('NETWORK_ANALYSIS.LINK_SINGULAR') : this.t('NETWORK_ANALYSIS.LINKS_PLURAL');
      return this.t('NETWORK_ANALYSIS.HIGHLIGHT_STATUS_BOTH', { n, nLabel, l, lLabel });
    } else if (n > 0) {
      const nLabel = n === 1 ? this.t('NETWORK_ANALYSIS.NODE_SINGULAR') : this.t('NETWORK_ANALYSIS.NODES_PLURAL');
      return this.t('NETWORK_ANALYSIS.HIGHLIGHT_STATUS_NODES', { n, nLabel });
    } else if (l > 0) {
      const lLabel = l === 1 ? this.t('NETWORK_ANALYSIS.LINK_SINGULAR') : this.t('NETWORK_ANALYSIS.LINKS_PLURAL');
      return this.t('NETWORK_ANALYSIS.HIGHLIGHT_STATUS_LINKS', { l, lLabel });
    }
    return '';
  }

  clearHighlights(): void {

    this.updateHighlight(null);
  }

  // Proper clearSelection API used by the template/toolbar
  clearSelection(): void {
    this.selected = null;
    this.clearHighlights();
  }


  onSearchInput(): void {
    if (!this.searchQuery.trim()) {
      this.searchResults = [];
      this.selectedSearchResult = null;
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    this.searchResults = this.graphData.nodes.filter(node => {

      const searchableText = [
        node.name || '',
        node.id || '',
        node.group || '',
        ...(node.labels || [])
      ].join(' ').toLowerCase();

      return searchableText.includes(query);
    }).slice(0, 10);


    this.selectedSearchResult = null;
  }

  searchAndFocus(): void {
    if (this.searchResults.length > 0) {

      const targetResult = this.selectedSearchResult || this.searchResults[0];
      this.focusOnNode(targetResult);
    }
  }

  focusOnNode(result: any): void {
    this.selectedSearchResult = result;

    if (this.graphInstance && result) {

      this.clearHighlights();


      this.updateHighlight(result);


      this.graphInstance.centerAt(result.x || 0, result.y || 0, 1000);
      this.graphInstance.zoom(3, 1000);


    }
  }

  focusOnStatusBarNode(): void {
    const label = this.statusBarNodeLabel;
    if (!label) {
      return;
    }

    const node = this.findNodeByLabel(label);
    if (node) {
      this.searchQuery = '';
      this.searchResults = [];
      this.selectedSearchResult = null;
      this.focusOnNode(node);
    }
  }

  private findNodeByLabel(label: string): any | null {
    const normalized = label.toLowerCase().trim();
    if (!normalized || !this.graphData?.nodes?.length) {
      return null;
    }

    const nodes = this.graphData.nodes;

    const exactMatch = nodes.find(node => {
      const name = String(node.name ?? '').toLowerCase();
      const id = String(node.id ?? '').toLowerCase();
      return name === normalized || id === normalized;
    });

    if (exactMatch) {
      return exactMatch;
    }

    return nodes.find(node => {
      const searchableText = [
        node.name || '',
        node.id || '',
        node.group || '',
        ...(node.labels || [])
      ].join(' ').toLowerCase();

      return searchableText.includes(normalized);
    }) || null;
  }

  focusOnSelectedNode(): void {
    if (!this.selectedNode) {
      return;
    }
    this.focusOnNode(this.selectedNode);
  }

  // Execute a fast-mode neighborhood query around a Provider by its internal id (preferred) or Name
  onSelectProvider(p: { id: number; name: string; elementId?: string } | null): void {
    if (!p) { return; }
    this.selectedProvider = p;
    this.showSplashScreen = false;

    // Prefer stable elementId, then internal id(n), then name as last resort.
    const maxPaths = 600;
    const elementId = (p.elementId || '').trim();
    if (elementId) {
      this.cypher = `MATCH (n) WHERE elementId(n) = $elementId
        MATCH p = (n)-[*..2]-(m)
        RETURN nodes(p) AS nodes, relationships(p) AS rels
        LIMIT $maxPaths`;
      this.params = { elementId, maxPaths };
    } else if (typeof p.id === 'number' && isFinite(p.id) && p.id > 0) {
      const neoId = (neo4j as any).int ? (neo4j as any).int(Math.floor(p.id)) : Math.floor(p.id);
      this.cypher = `MATCH (n) WHERE id(n) = $id
        MATCH p = (n)-[*..2]-(m)
        RETURN nodes(p) AS nodes, relationships(p) AS rels
        LIMIT $maxPaths`;
      this.params = { id: neoId, maxPaths };
    } else {
      const fallbackLabel = this.config?.primaryEntityLabel || 'n';
      const fallbackNameExpr = this.buildNameExprForLabel(fallbackLabel, 'n');
      this.cypher = `MATCH (n:${fallbackLabel})
        WHERE toLower(${fallbackNameExpr}) CONTAINS toLower($name)
        MATCH p = (n)-[*..2]-(m)
        RETURN nodes(p) AS nodes, relationships(p) AS rels
        LIMIT toInteger($maxPaths)`;
      this.params = { name: p.name, maxPaths };
    }
        this.currentQueryDescription = `${this.t('NETWORK_ANALYSIS.NEIGHBORHOOD_AROUND_A_NODE')} (2 hops - ${p.name})`;

    this.isLoading = true;
    this.fetchGraphData()
      .then(async ({ nodes, links }) => {
        this.graphData = { nodes, links };
        
        // Ensure graph instance is ready before updating
        await this.ensureGraphInstance();
        
        if (this.graphInstance) {
          try { this.graphInstance.pauseAnimation?.(); } catch { }
          this.resetNodePhysics(this.graphData.nodes);
          this.graphInstance.graphData({ nodes: this.graphData.nodes, links: this.graphData.links });
          this.configureForces();

          // IMPORTANT: Force the simulation to start properly (this was the key fix!)
          setTimeout(() => {

            this.graphInstance.d3ReheatSimulation();

            // Set a higher alpha target to ensure simulation runs
            if (this.graphInstance.d3AlphaTarget) {
              this.graphInstance.d3AlphaTarget(0.3);
            }

            // Resume animation in case it was paused
            this.graphInstance.resumeAnimation();
          }, 50);
          setTimeout(() => {
            // Restart the simulation to ensure nodes get positions
            this.graphInstance.d3ReheatSimulation();
          }, 50);

          // Ensure nodes are not pinned from a previous view
          this.unpinAllNodes();
          // Re-apply center force to container center and then fit
          const container = this.container?.nativeElement as HTMLElement | undefined;
          if (container && (window as any).d3?.forceCenter) {
            const rect = container.getBoundingClientRect();
            this.graphInstance.d3Force('center', (window as any).d3.forceCenter(rect.width / 2, rect.height / 2));

            // Auto-fit viewport to show all nodes
            if (nodes.length > 0) {
              setTimeout(() => {
                // Try a simpler approach - just center and zoom to a reasonable level
                const container = this.container?.nativeElement;
                const cx = container ? container.clientWidth / 2 : 679;
                const cy = container ? container.clientHeight / 2 : 400;

                this.graphInstance.centerAt(cx, cy, 300);
                this.graphInstance.zoom(1, 300);

                // Also try the original method
                setTimeout(() => {
                  this.safeZoomToFit(300);
                }, 500);
              }, 300);
            }
          }
        } else {
          console.warn('[Neo4j] No graph instance available to update');
        }
        this.isLoading = false;
      })
      .catch((err) => {
        console.error('Provider query error', err);
        this.isLoading = false;
      });
  }


  private computeHeaderOffset() {
    // Remove gap between navbar and graph: force zero offset
    this.headerOffset = 0;
  }

  private t(key: string, params?: Record<string, any>) {
    return this.translate.instant(key, params);
  }

  // Execute query to fetch top 20 nodes by degree for selected entity type
  onSelectTop20Entity(entityType: string | null): void {
    if (!entityType) { return; }
    this.selectedTop20Entity = entityType;
    this.showSplashScreen = false;

    // Apply spread preset for better visualization of dense Top 20 networks
    this.applyPreset('spread');

    // Query to get top 20 nodes and their top 20 relationships each
    this.cypher = `
      MATCH (n:${entityType})
      WITH n, COUNT { (n)--() } as degree
      WHERE degree > 0
      ORDER BY degree DESC
      LIMIT 20
      WITH collect(n) as topNodes
      UNWIND topNodes as n
      MATCH path = (n)-[r]-(m)
      WITH n, path
      ORDER BY n
      WITH n, collect(path)[0..20] as paths
      UNWIND paths as path
      RETURN nodes(path) AS nodes, relationships(path) AS rels
    `;
    this.params = {};
    this.currentQueryDescription = `${this.t('NETWORK_ANALYSIS.TOP_20')} ${entityType} ${this.t('NETWORK_ANALYSIS.NODES')}`;

    this.isLoading = true;
    this.fetchGraphData()
      .then(async ({ nodes, links }) => {
        this.graphData = { nodes, links };
        
        // Ensure graph instance is ready before updating
        await this.ensureGraphInstance();
        
        if (this.graphInstance) {
          try { this.graphInstance.pauseAnimation?.(); } catch { }
          this.resetNodePhysics(this.graphData.nodes);
          this.graphInstance.graphData({ nodes: this.graphData.nodes, links: this.graphData.links });
          this.configureForces();

          setTimeout(() => {
            this.graphInstance.d3ReheatSimulation();
            if (this.graphInstance.d3AlphaTarget) {
              this.graphInstance.d3AlphaTarget(0.3);
            }
            this.graphInstance.resumeAnimation();
          }, 50);

          this.unpinAllNodes();
          const container = this.container?.nativeElement as HTMLElement | undefined;
          if (container && (window as any).d3?.forceCenter) {
            const rect = container.getBoundingClientRect();
            this.graphInstance.d3Force('center', (window as any).d3.forceCenter(rect.width / 2, rect.height / 2));

            if (nodes.length > 0) {
              setTimeout(() => {
                const container = this.container?.nativeElement;
                const cx = container ? container.clientWidth / 2 : 679;
                const cy = container ? container.clientHeight / 2 : 400;
                this.graphInstance.centerAt(cx, cy, 1000);
                this.graphInstance.zoom(1.5, 1000);
              }, 300);
            }
          }
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      })
      .catch((err: any) => {
        console.error('Top 20 query error:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      });
  }
}
